import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { isDarkMode } from "../../../utils/constants";
import { isMobileBrowser } from "../../../utils/user_agent";
import {
  createMainSeriesData,
  formatCrosshairTime,
  getDesiredLogicalRange,
  getInitialLogicalRange,
  logicalRangesClose,
  normalizeChartScale,
  resetChartPriceScale,
  resolveChartScreenshotCanvas,
  zoomLogicalRange,
} from "./lightweightChartUtils";
import {
  installAttributionHider,
  loadLightweightCharts,
} from "./lightweightChartsLoader";
import {
  buildChartRangeIdentityKey,
  normalizeLogicalRange,
  normalizeTimeRange,
  preserveLogicalRangeForAppend,
  rebaseLogicalRangeForSeriesSwitch,
  releaseRangeSyncSuppression,
  restoreLogicalRangeFromSharedSnapshot,
  scheduleRangeApply,
  timeRangeOverlapsSeries,
} from "./chartRangeState";
import {
  applySeriesData,
  latestPointOnlyUpdateForDataChange,
} from "./chartSeriesUpdates";
import { getChartPalette as getPalette } from "./chartPalette";
import { buildChartOptions } from "./lightweightChartOptions";
import {
  clearOverlayCanvas,
  resizeOverlayCanvas,
} from "./chartOverlayCanvas";
import {
  SESSION_OVERLAY_EMPTY,
  buildSessionOverlayConfig,
  drawSessionOverlay,
} from "./chartSessionOverlay";
import {
  buildAgenticOverlayConfig,
  buildTradeOverlayConfig,
  drawAgenticOverlay,
  drawTradeOverlay,
  agenticOverlayStructureKeyForState,
  tradeOverlayContextKeyForMessage,
} from "./chartTradeOverlays";
import { installMacTrackpadZoomAmplifier } from "./chartWheelInteraction";
import { draftOverlayAutoSelection } from "./chartTradeDraftSelection";
import {
  DRAWING_MIN_DRAG_PX,
  FREEHAND_DRAWING_KINDS,
  FREEHAND_MAX_CAPTURE_POINTS,
  applyHorizontalLineOverlayToPriceLine,
  appendFreehandSample,
  buildEditableOverlayGeometry,
  duplicateEditableOverlay,
  drawingAnchorForChartPoint,
  drawingGeometryLength,
  freehandOverlayFromSamples,
  hitTestEditableOverlay,
  isCopyableEditableOverlay,
  moveEditableOverlay,
} from "./chartDrawingOverlays";
import { buildIndicatorSeriesConfigs } from "./chartIndicatorSeries";
import {
  VOLUME_CANDLE_VIEW,
  buildVolumeCandleOverlayConfig,
  drawVolumeCandles,
  extendAutoscaleInfoWithVolumeCandleLayout,
  findPackedVolumeCandle,
  resolveVolumeCandleLayout,
  summarizePackedVolumeCandleGeometry,
} from "./chartVolumeCandles";
import {
  buildCurrentPriceLineOptions,
  hideLivePriceLabel,
  resetOhlcBox,
  updateLivePriceLabel,
  updateOhlcBox,
} from "./chartPriceReadouts";
import { toTradeNumber } from "./chartOverlayUtils";
import {
  calculateTradeMetrics,
  formatSignedTradeCurrency,
  formatTradeQuantity,
  normalizeTradeQuantity,
} from "./chartTradeMetrics";
import { shouldRequestChartHistory } from "./chartHistoryPaging";
import {
  buildProvisionalLiveCandle,
  provisionalCandleExtendsSeries,
} from "./chartProvisionalLiveCandle";
import { buildMarketReferencePriceLines } from "./marketPriceLines";
import ChartDrawingInspector from "./ChartDrawingInspector";
import ChartDrawingToolbar from "./ChartDrawingToolbar";

const CHART_DRAWING_CREATION_KINDS = [
  "horizontal_line",
  "trend_line",
  "rectangle",
  "circle",
  "parabola",
  "semicircle",
  ...FREEHAND_DRAWING_KINDS,
];

const CHART_EDITABLE_OVERLAY_KINDS = [
  ...CHART_DRAWING_CREATION_KINDS,
  "price_zone",
  "price_zone_box",
  "trade_setup",
];

const drawOverlayCanvas = ({
  chart,
  series,
  sessionOverlay,
  volumeCandleOverlay,
  tradeOverlay,
  agenticOverlay,
  selectedAgenticOverlayId,
  draftAgenticOverlay,
  canvas,
}) => {
  if (!canvas) {
    return { volumeCandleLayout: null, editableOverlayGeometry: [] };
  }

  const resized = resizeOverlayCanvas(canvas);
  if (!resized) {
    return { volumeCandleLayout: null, editableOverlayGeometry: [] };
  }

  const { context, width, height } = resized;
  context.clearRect(0, 0, width, height);

  drawSessionOverlay({
    chart,
    overlay: sessionOverlay,
    context,
    width,
    height,
  });
  const volumeCandleLayout = drawVolumeCandles({
    chart,
    series,
    overlay: volumeCandleOverlay,
    context,
    width,
    height,
  });
  drawTradeOverlay({
    chart,
    series,
    overlay: tradeOverlay,
    context,
    width,
    height,
  });
  const editableOverlayGeometry = drawAgenticOverlay({
    chart,
    series,
    overlay: agenticOverlay,
    context,
    width,
    height,
    selectedId: selectedAgenticOverlayId,
    draftOverlay: draftAgenticOverlay,
  });
  return {
    volumeCandleLayout,
    editableOverlayGeometry,
  };
};

const formatTradePrice = (value) => {
  const price = Number(value);
  if (!Number.isFinite(price)) return "--";
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.abs(price) < 10 ? 4 : 2,
  });
};

const formatTradeTimestamp = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const ChartCanvas = memo(
  ({
    idx,
    messageThread,
    chartConfigKey,
    hasRendered,
    setHasRendered,
    selectedScale,
    chartViewType,
    tickerPrices,
    assetType: assetTypeOverride,
    extendedHoursQuote = null,
    livePriceQuote = null,
    priceMarkers = [],
    isChartVisible = true,
    selectedTickerId = null,
    tradeOverlayKey = "",
    agenticChartState = null,
    agenticOverlayKey = "",
    compactAgenticLabels = false,
    explainBarChange = false,
    showIndicators = true,
    showAgenticOverlays = true,
    editableAgenticOverlays = false,
    agenticOverlayDrawingMode = null,
    onAgenticOverlayCreate = null,
    onAgenticOverlayChange = null,
    onAgenticOverlayDelete = null,
    onAgenticOverlayHide = null,
    onAgenticOverlayAsk = null,
    onAgenticOverlayUseInPlan = null,
    onAgenticOverlayCreateMonitor = null,
    resolveAgenticOverlayPlanContext = null,
    agenticOverlayContextLabel = null,
    agenticOverlayBusy = false,
    agenticOverlayTickSize = null,
    onDrawingUndo = null,
    onDrawingRedo = null,
    onAgenticOverlayReviewPlan = null,
    onAgenticOverlayDrawingCancel = null,
    draftAgenticOverlayId = null,
    onAgenticOverlayDraftSubmit = null,
    onAgenticOverlayDraftCancel = null,
    onAgenticOverlayDraftStart = null,
    onChartContextMenu = null,
    sharedRangeBus = null,
    rangeTransfer = null,
    preferUpdatedDisplayRange = false,
    onRequestHistory = null,
    historyLoading = false,
    historyHasMore = false,
    onChartReady,
    canvasId,
    canvasStyle,
    userTimezone = null,
  }) => {
    const isMobile = isMobileBrowser();
    const [isNarrowViewport, setIsNarrowViewport] = useState(
      () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    );
    const scale = normalizeChartScale(selectedScale);
    const assetType = assetTypeOverride || messageThread?.assetType || null;
    const chartRootRef = useRef(null);
    const drawingLayoutRef = useRef(null);
    const [drawingLayoutWidth, setDrawingLayoutWidth] = useState(0);
    const [drawingDetailsTab, setDrawingDetailsTab] = useState(null);
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const chartHandleRef = useRef(null);
    const seriesRef = useRef(null);
    const seriesKindRef = useRef(null);
    const priceLineRefsRef = useRef([]);
    const livePriceLineRef = useRef(null);
    const provisionalLiveCandleRef = useRef(null);
    const indicatorSeriesRefsRef = useRef([]);
    const resizeObserverRef = useRef(null);
    const attributionObserverRef = useRef(null);
    const pointCountRef = useRef(0);
    const previousChartIdentityRef = useRef(null);
    const previousRangeCompatibilityRef = useRef(null);

    useEffect(() => {
      if (typeof window === "undefined") return undefined;
      const media = window.matchMedia("(max-width: 767px)");
      const sync = () => setIsNarrowViewport(media.matches);
      sync();
      media.addEventListener?.("change", sync);
      return () => media.removeEventListener?.("change", sync);
    }, []);
    useEffect(() => {
      const root = drawingLayoutRef.current;
      if (!root) return undefined;
      const observer = new ResizeObserver(() => setDrawingLayoutWidth(root.clientWidth));
      observer.observe(root);
      setDrawingLayoutWidth(root.clientWidth);
      return () => observer.disconnect();
    }, []);
    const previousSeriesMetaRef = useRef({
      firstTime: null,
      pointCount: 0,
    });
    const previousPriceSeriesDataRef = useRef({
      kind: null,
      candleData: [],
      lineData: [],
    });
    const previousIndicatorSeriesConfigsRef = useRef([]);
    const previousLiveUpdateContextRef = useRef(null);
    const previousDesiredRangeApplicationRef = useRef(null);
    const suppressRangeSyncRef = useRef(false);
    const historyRequestInFlightRef = useRef(false);
    const onRequestHistoryRef = useRef(onRequestHistory);
    const historyLoadingRef = useRef(historyLoading);
    const historyHasMoreRef = useRef(historyHasMore);
    const syncIdRef = useRef(`chart-sync-${idx}-${canvasId || "main"}`);
    const ohlcRef = useRef(null);
    const volumeCandleTimeLabelRef = useRef(null);
    const livePriceLabelRef = useRef(null);
    const tradeOverlayCanvasRef = useRef(null);
    const tradeOverlayConfigRef = useRef({
      segments: [],
      markers: [],
      priceLines: [],
    });
    const agenticOverlayConfigRef = useRef({
      priceLines: [],
      zones: [],
      editableOverlays: [],
      priceTimes: [],
    });
    const agenticPriceLineRefsRef = useRef(new Map());
    const editableOverlayGeometryRef = useRef([]);
    const overlayInteractionRef = useRef(null);
    const overlayCommitPendingRef = useRef(false);
    const draftAgenticOverlayRef = useRef(null);
    const selectedAgenticOverlayRef = useRef(null);
    const copiedAgenticOverlayRef = useRef(null);
    const autoSelectedDraftOverlayIdRef = useRef(null);
    const overlayInteractionOptionsRef = useRef(null);
    const overlayPointerHandlersRef = useRef({
      pointerDown: null,
      keyDown: null,
      contextMenu: null,
    });
    const tradeOverlayDrawRef = useRef(() => {});
    const tradeOverlayFrameRef = useRef(null);
    const chartResizeFrameRef = useRef(null);
    const sessionOverlayConfigRef = useRef(SESSION_OVERLAY_EMPTY);
    const volumeCandleOverlayConfigRef = useRef({
      enabled: false,
      candles: [],
      referenceVolume: null,
    });
    const volumeCandleLayoutRef = useRef(null);
    const candleLookupRef = useRef(new Map());
    const latestCandleRef = useRef(null);
    const livePriceQuoteRef = useRef(livePriceQuote);
    const [library, setLibrary] = useState(
      () => window.LightweightCharts || null
    );
    const [loadError, setLoadError] = useState(null);
    const [selectedAgenticOverlay, setSelectedAgenticOverlay] = useState(null);
    const [tradeExecutionTargets, setTradeExecutionTargets] = useState([]);
    const tradeExecutionTargetsKeyRef = useRef("");

    onRequestHistoryRef.current = onRequestHistory;
    historyLoadingRef.current = historyLoading;
    historyHasMoreRef.current = historyHasMore;
    livePriceQuoteRef.current = livePriceQuote;

    selectedAgenticOverlayRef.current = selectedAgenticOverlay;
    overlayInteractionOptionsRef.current = {
      editable: editableAgenticOverlays,
      busy: agenticOverlayBusy,
      drawingMode: agenticOverlayDrawingMode,
      onCreate: onAgenticOverlayCreate,
      onChange: onAgenticOverlayChange,
      onDelete: onAgenticOverlayDelete,
      onHide: onAgenticOverlayHide,
      onCancelDrawing: onAgenticOverlayDrawingCancel,
      draftId: draftAgenticOverlayId,
      onDraftCancel: onAgenticOverlayDraftCancel,
      onDraftStart: onAgenticOverlayDraftStart,
      onContextMenu: onChartContextMenu,
      onUndo: onDrawingUndo,
      onRedo: onDrawingRedo,
    };

    const priceSeries = useMemo(
      () => createMainSeriesData(tickerPrices || [], userTimezone),
      [tickerPrices, userTimezone]
    );
    const provisionalLiveCandle = useMemo(
      () => buildProvisionalLiveCandle({
        tickerPrices,
        scale,
        chartViewType,
        assetType,
        livePriceQuote,
        previousProvisionalCandle: provisionalLiveCandleRef.current,
        timeZone: userTimezone,
      }),
      [
        assetType,
        chartViewType,
        livePriceQuote,
        scale,
        tickerPrices,
        userTimezone,
      ],
    );

    pointCountRef.current = priceSeries.times.length;

    const scheduleTradeOverlayDraw = () => {
      if (typeof window === "undefined") return;
      if (tradeOverlayFrameRef.current != null) return;
      tradeOverlayFrameRef.current = window.requestAnimationFrame(() => {
        tradeOverlayFrameRef.current = null;
        tradeOverlayDrawRef.current?.();
      });
    };

    const setOverlayCursor = (cursor = "") => {
      if (chartRootRef.current) chartRootRef.current.style.cursor = cursor;
      if (containerRef.current) containerRef.current.style.cursor = cursor;
    };

    const selectEditableOverlay = (overlay) => {
      if (String(overlay?.id || "") !== String(selectedAgenticOverlayRef.current?.id || "")) setDrawingDetailsTab(null);
      const selection = overlay?.id
        ? {
            ...overlay,
            id: String(overlay.id),
            kind: String(overlay.kind || "overlay"),
            label:
              overlay.label ||
              String(overlay.semantic_role || overlay.semanticRole || "Drawing")
                .replaceAll("_", " ")
                .replace(/\b\w/g, (letter) => letter.toUpperCase()),
          }
        : null;
      selectedAgenticOverlayRef.current = selection;
      setSelectedAgenticOverlay(selection);
      scheduleTradeOverlayDraw();
    };

    const selectedCopyableOverlay = () => {
      const selected = selectedAgenticOverlayRef.current;
      if (!selected?.id) return null;
      const overlay =
        agenticOverlayConfigRef.current?.editableOverlays?.find(
          (row) => String(row?.id) === String(selected.id),
        ) || selected;
      return isCopyableEditableOverlay(overlay) ? overlay : null;
    };

    const copySelectedOverlay = () => {
      const overlay = selectedCopyableOverlay();
      if (!overlay) return false;
      copiedAgenticOverlayRef.current = { ...overlay };
      return true;
    };

    const pasteCopiedOverlay = () => {
      const source = copiedAgenticOverlayRef.current;
      const options = overlayInteractionOptionsRef.current;
      if (!source || typeof options?.onCreate !== "function") return false;
      const duplicate = duplicateEditableOverlay({
        overlay: source,
        priceTimes: priceSeries.times,
      });
      if (!duplicate) return false;

      const previousSelection = selectedAgenticOverlayRef.current;
      overlayCommitPendingRef.current = true;
      draftAgenticOverlayRef.current = duplicate;
      selectEditableOverlay(duplicate);
      let result = false;
      try {
        result = options.onCreate(duplicate);
      } catch (_error) {
        result = false;
      }
      Promise.resolve(result)
        .then((saved) => {
          clearOverlayInteraction();
          if (saved === false) {
            selectEditableOverlay(previousSelection);
            return;
          }
          copiedAgenticOverlayRef.current = { ...duplicate };
          selectEditableOverlay(duplicate);
        })
        .catch(() => {
          clearOverlayInteraction();
          selectEditableOverlay(previousSelection);
        });
      return true;
    };

    const applyDraftPriceLine = (overlay) => {
      applyHorizontalLineOverlayToPriceLine({
        overlay,
        priceLineById: agenticPriceLineRefsRef.current,
      });
    };

    const restorePriceLine = (overlay) => {
      applyHorizontalLineOverlayToPriceLine({
        overlay,
        priceLineById: agenticPriceLineRefsRef.current,
      });
    };

    const clearOverlayInteraction = ({
      restoreOverlay = null,
      clearSelection = false,
    } = {}) => {
      if (restoreOverlay) restorePriceLine(restoreOverlay);
      overlayInteractionRef.current = null;
      overlayCommitPendingRef.current = false;
      draftAgenticOverlayRef.current = null;
      if (clearSelection) selectEditableOverlay(null);
      const drawingMode = overlayInteractionOptionsRef.current?.drawingMode;
      setOverlayCursor(drawingMode ? "crosshair" : "");
      scheduleTradeOverlayDraw();
    };

    const syncLivePriceLine = () => {
      const series = seriesRef.current;
      const chart = chartRef.current;
      const livePrice = toTradeNumber(livePriceQuote?.price);
      if (!series || !chart) {
        livePriceLineRef.current = null;
        return;
      }

      if (livePrice == null) {
        if (livePriceLineRef.current) {
          try {
            series.removePriceLine?.(livePriceLineRef.current);
          } catch (_error) {}
          livePriceLineRef.current = null;
        }
        series.applyOptions?.(
          buildCurrentPriceLineOptions({
            library,
            latestCandle: latestCandleRef.current,
            palette: getPalette(),
          }),
        );
        delete chartRootRef.current?.dataset.chartLivePrice;
        delete chartRootRef.current?.dataset.chartLivePriceUpdatedAt;
        delete chartRootRef.current?.dataset.chartLivePriceRole;
        return;
      }

      const palette = getPalette();
      const open = toTradeNumber(latestCandleRef.current?.open);
      const livePriceRole = String(livePriceQuote?.role || "live");
      const extendedHoursLivePrice =
        livePriceRole === "extended_hours" || extendedHoursQuote != null;
      const color = extendedHoursLivePrice
        ? palette.extendedHours
        : open == null || livePrice >= open
          ? palette.up
          : palette.down;
      const options = {
        price: livePrice,
        color,
        lineWidth: 1,
        lineStyle: library?.LineStyle?.Dotted ?? 1,
        axisLabelVisible: true,
        title: "",
      };
      series.applyOptions?.({ priceLineVisible: false });
      if (livePriceLineRef.current) {
        livePriceLineRef.current.applyOptions?.(options);
      } else {
        livePriceLineRef.current = series.createPriceLine?.(options) || null;
      }
      if (chartRootRef.current) {
        chartRootRef.current.dataset.chartLivePrice = String(livePrice);
        chartRootRef.current.dataset.chartLivePriceRole = livePriceRole;
        if (livePriceQuote?.quoteAtIso) {
          chartRootRef.current.dataset.chartLivePriceUpdatedAt = String(
            livePriceQuote.quoteAtIso,
          );
        } else {
          delete chartRootRef.current.dataset.chartLivePriceUpdatedAt;
        }
      }
    };

    const syncProvisionalLiveCandle = () => {
      const series = seriesRef.current;
      const chartRoot = chartRootRef.current;
      const projectedCandle = provisionalLiveCandle?.candle || null;
      if (
        !series ||
        seriesKindRef.current !== "candlestick" ||
        !projectedCandle
      ) {
        provisionalLiveCandleRef.current = null;
        if (chartRoot) {
          delete chartRoot.dataset.chartProvisionalCandlePrice;
          delete chartRoot.dataset.chartProvisionalCandleUpdatedAt;
        }
        return;
      }

      try {
        series.update(projectedCandle);
      } catch (_error) {
        return;
      }

      provisionalLiveCandleRef.current = projectedCandle;
      latestCandleRef.current = projectedCandle;
      if (chartRoot) {
        chartRoot.dataset.chartProvisionalCandlePrice = String(
          projectedCandle.close,
        );
        chartRoot.dataset.chartProvisionalCandleUpdatedAt = String(
          livePriceQuote?.quoteAtIso || "",
        );
      }

      const volumeOverlay = volumeCandleOverlayConfigRef.current;
      if (volumeOverlay?.enabled && Array.isArray(volumeOverlay.candles)) {
        const existingCandles = volumeOverlay.candles;
        const lastCandle = existingCandles.at(-1);
        const projectedCandles =
          lastCandle?.time === projectedCandle.time
            ? existingCandles.slice(0, -1).concat(projectedCandle)
            : lastCandle?.time < projectedCandle.time
              ? existingCandles.concat(projectedCandle)
              : existingCandles;
        volumeCandleOverlayConfigRef.current = {
          ...volumeOverlay,
          candles: projectedCandles,
        };
      }

      updateOhlcBox(ohlcRef.current, projectedCandle, assetType, {
        explainChange: explainBarChange,
      });
      scheduleTradeOverlayDraw();
    };

    const syncLivePriceLabel = () => {
      updateLivePriceLabel({
        element: livePriceLabelRef.current,
        chart: chartRef.current,
        series: seriesRef.current,
        latestCandle: latestCandleRef.current,
        scale,
        assetType,
        isMobile,
        extendedHoursQuote,
        livePriceQuote: livePriceQuoteRef.current,
        timeZone: userTimezone,
      });
    };

    const clearIndicatorSeries = (chart = chartRef.current) => {
      if (!chart) {
        indicatorSeriesRefsRef.current = [];
        return;
      }
      indicatorSeriesRefsRef.current.forEach((indicatorSeries) => {
        try {
          chart.removeSeries?.(indicatorSeries);
        } catch (_error) {}
      });
      indicatorSeriesRefsRef.current = [];
    };

    const syncVisibleRangeMetadata = (range) => {
      const root = chartRootRef.current;
      if (!root) return;

      const normalized = normalizeLogicalRange(range);
      root.dataset.chartPointCount = String(pointCountRef.current || 0);
      if (!normalized) {
        delete root.dataset.visibleLogicalSpan;
        delete root.dataset.visibleLogicalFrom;
        delete root.dataset.visibleLogicalTo;
        return;
      }
      root.dataset.visibleLogicalFrom = String(normalized.from);
      root.dataset.visibleLogicalTo = String(normalized.to);
      root.dataset.visibleLogicalSpan = String(
        normalized.to - normalized.from
      );
    };

    const syncVolumeCandleLayoutMetadata = (layout) => {
      const root = chartRootRef.current;
      if (!root) return;
      const setDatasetValue = (name, value) => {
        const nextValue = String(value);
        if (root.dataset[name] !== nextValue) {
          root.dataset[name] = nextValue;
        }
      };

      const summary = summarizePackedVolumeCandleGeometry(layout);
      if (summary.count === 0) {
        delete root.dataset.volumeCandleLayout;
        delete root.dataset.volumeCandleCount;
        delete root.dataset.volumeCandleGapMin;
        delete root.dataset.volumeCandleGapMax;
        delete root.dataset.volumeCandleWidthMin;
        delete root.dataset.volumeCandleWidthMax;
        delete root.dataset.volumeCandleOverlapCount;
        delete root.dataset.volumeCandlePanOffset;
        delete root.dataset.volumeCandleBarSpacing;
        delete root.dataset.volumeCandleGeometryScale;
        delete root.dataset.volumeCandleAnchorIndex;
        delete root.dataset.volumeCandleAnchorX;
        delete root.dataset.volumeCandleAnchorWidth;
        delete root.dataset.volumeCandlePriceMin;
        delete root.dataset.volumeCandlePriceMax;
        delete root.dataset.volumeCandlePriceCoordinateMin;
        delete root.dataset.volumeCandlePriceCoordinateMax;
        delete root.dataset.volumeCandlePlotHeight;
        delete root.dataset.volumeCandlePriceOutOfBoundsCount;
        return;
      }

      setDatasetValue("volumeCandleLayout", "packed");
      setDatasetValue("volumeCandleCount", summary.count);
      setDatasetValue("volumeCandleGapMin", summary.gapMin);
      setDatasetValue("volumeCandleGapMax", summary.gapMax);
      setDatasetValue("volumeCandleWidthMin", summary.widthMin);
      setDatasetValue("volumeCandleWidthMax", summary.widthMax);
      setDatasetValue("volumeCandleOverlapCount", summary.overlapCount);
      setDatasetValue("volumeCandlePanOffset", layout.panOffset);
      setDatasetValue("volumeCandleBarSpacing", layout.barSpacing);
      setDatasetValue("volumeCandleGeometryScale", layout.geometryScale);
      setDatasetValue("volumeCandleAnchorIndex", layout.anchorIndex);
      setDatasetValue("volumeCandleAnchorX", layout.anchorX);
      setDatasetValue("volumeCandleAnchorWidth", layout.anchorWidth);
      setDatasetValue("volumeCandlePriceMin", layout.priceRange?.minValue);
      setDatasetValue("volumeCandlePriceMax", layout.priceRange?.maxValue);
      setDatasetValue(
        "volumeCandlePriceCoordinateMin",
        layout.priceCoordinateMin,
      );
      setDatasetValue(
        "volumeCandlePriceCoordinateMax",
        layout.priceCoordinateMax,
      );
      setDatasetValue("volumeCandlePlotHeight", layout.plotHeight);
      setDatasetValue(
        "volumeCandlePriceOutOfBoundsCount",
        layout.priceOutOfBoundsCount,
      );
    };

    const syncEditableOverlayGeometryMetadata = (geometry) => {
      const root = chartRootRef.current;
      if (!root) return;
      const rows = (Array.isArray(geometry) ? geometry : []).map((item) => ({
        id: item.id,
        kind: item.kind,
        x1: Number(item.x1),
        y1: Number(item.y1),
        x2: Number(item.x2),
        y2: Number(item.y2),
        entryY: Number.isFinite(item.entryY) ? Number(item.entryY) : undefined,
        stopY: Number.isFinite(item.stopY) ? Number(item.stopY) : undefined,
        targetY: Number.isFinite(item.targetY) ? Number(item.targetY) : undefined,
        entryX: Number.isFinite(item.entryPoint?.x) ? Number(item.entryPoint.x) : undefined,
        entryExecutionY: Number.isFinite(item.entryPoint?.y) ? Number(item.entryPoint.y) : undefined,
        exitX: Number.isFinite(item.exitPoint?.x) ? Number(item.exitPoint.x) : undefined,
        exitExecutionY: Number.isFinite(item.exitPoint?.y) ? Number(item.exitPoint.y) : undefined,
      }));
      const executionTargets = (Array.isArray(geometry) ? geometry : [])
        .filter((item) => item.kind === "trade_execution")
        .flatMap((item) => [
          item.entryPoint
            ? { id: String(item.id), node: "entry", x: item.entryPoint.x, y: item.entryPoint.y }
            : null,
          item.exitPoint
            ? { id: String(item.id), node: "exit", x: item.exitPoint.x, y: item.exitPoint.y }
            : null,
        ])
        .filter(Boolean);
      const executionTargetsKey = JSON.stringify(executionTargets);
      if (tradeExecutionTargetsKeyRef.current !== executionTargetsKey) {
        tradeExecutionTargetsKeyRef.current = executionTargetsKey;
        setTradeExecutionTargets(executionTargets);
      }
      root.dataset.chartDrawingGeometryCount = String(rows.length);
      if (rows.length === 0) {
        delete root.dataset.chartDrawingGeometry;
        return;
      }
      root.dataset.chartDrawingGeometry = JSON.stringify(rows);
    };

    const hideVolumeCandleTimeLabel = () => {
      if (volumeCandleTimeLabelRef.current) {
        volumeCandleTimeLabelRef.current.style.display = "none";
      }
    };

    const updateVolumeCandleTimeLabel = ({ candle, x }) => {
      const element = volumeCandleTimeLabelRef.current;
      const layout = volumeCandleLayoutRef.current;
      if (!element || !candle || !layout || !Number.isFinite(x)) {
        hideVolumeCandleTimeLabel();
        return;
      }

      const label = formatCrosshairTime(candle.time, scale);
      if (!label) {
        hideVolumeCandleTimeLabel();
        return;
      }

      element.textContent = label;
      element.style.display = "block";
      const width = Math.max(element.offsetWidth || 120, 60);
      const leftBoundary = Number(layout.left) || 0;
      const rightBoundary = Number(layout.right) || chartRootRef.current?.clientWidth || 0;
      const clampedX = Math.max(
        leftBoundary + (width / 2),
        Math.min(x, rightBoundary - (width / 2)),
      );
      element.style.left = `${clampedX}px`;
    };

    useEffect(() => {
      let cancelled = false;

      loadLightweightCharts()
        .then((api) => {
          if (cancelled) return;
          setLibrary(api);
        })
        .catch((error) => {
          if (cancelled) return;
          setLoadError(error);
        });

      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      const drawingMode = agenticOverlayDrawingMode;
      if (
        !overlayInteractionRef.current &&
        !overlayCommitPendingRef.current
      ) {
        if (!drawingMode) {
          draftAgenticOverlayRef.current = null;
        }
        setOverlayCursor(drawingMode ? "crosshair" : "");
        scheduleTradeOverlayDraw();
      }
    }, [agenticOverlayDrawingMode]);

    useEffect(() => {
      const autoSelection = draftOverlayAutoSelection({
        draftId: draftAgenticOverlayId,
        lastAutoSelectedDraftId: autoSelectedDraftOverlayIdRef.current,
      });
      autoSelectedDraftOverlayIdRef.current = autoSelection.nextLastAutoSelectedDraftId;

      const selectedId = autoSelection.shouldAutoSelect
        ? autoSelection.draftId
        : selectedAgenticOverlayRef.current?.id;
      if (!selectedId) return;
      const overlay = Array.isArray(agenticChartState?.overlays)
        ? agenticChartState.overlays.find(
            (row) => String(row?.id) === String(selectedId),
          )
        : null;
      if (!overlay && String(draftAgenticOverlayRef.current?.id) !== String(selectedId)) {
        selectEditableOverlay(null);
        return;
      }
      if (overlay) selectEditableOverlay(overlay);
    }, [agenticChartState, agenticOverlayKey, draftAgenticOverlayId]);

    useEffect(() => {
      if (!library || !isChartVisible || !containerRef.current) return undefined;

      const container = containerRef.current;
      const chart = library.createChart(container, {
        width: Math.max(container.clientWidth || 0, 320),
        // The parent owns pane geometry, including short phone panes. Use a
        // fallback only before it has a size; a minimum clips the time axis.
        height: Math.max(container.clientHeight || 220, 1),
        ...buildChartOptions({
          library,
          scale,
          assetType,
          isMobile,
          chartViewType,
        }),
      });

      chart.applyOptions({ attributionLogo: { visible: false } });
      chartRef.current = chart;

      const removeTrackpadZoomAmplifier = installMacTrackpadZoomAmplifier({
        // Keep this listener on Lightweight Charts' own surface so wheel use
        // over drawing controls or other chart UI retains normal behavior.
        element: isMobile ? null : container,
      });

      attributionObserverRef.current = installAttributionHider(container);
      resetOhlcBox(ohlcRef.current);
      hideLivePriceLabel(livePriceLabelRef.current);

      const publishSharedRange = (range, { delivery = "immediate" } = {}) => {
        if (!sharedRangeBus?.setRange) return;
        if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
          return;
        }
        if (pointCountRef.current <= 0) return;
        sharedRangeBus.setRange(range, syncIdRef.current, {
          pointCount: pointCountRef.current,
          scale,
        }, { delivery });
      };

      const pushSharedRange = (range) => {
        if (suppressRangeSyncRef.current) return;
        // Trackpads can emit a burst of logical-range changes within one paint.
        // The source chart already renders each native gesture; only the linked
        // chart needs frame-coalesced updates to stay smooth.
        publishSharedRange(range, { delivery: "frame" });
      };

      const timeScale = chart.timeScale();
      const handleVisibleRangeChange = (range) => {
        syncVisibleRangeMetadata(range);
        pushSharedRange(range);
        scheduleTradeOverlayDraw();
        syncLivePriceLabel();
        if (
          typeof onRequestHistoryRef.current === "function" &&
          !historyRequestInFlightRef.current &&
          shouldRequestChartHistory({
            range,
            pointCount: pointCountRef.current,
            hasMore: historyHasMoreRef.current,
            loading: historyLoadingRef.current,
          })
        ) {
          historyRequestInFlightRef.current = true;
          Promise.resolve()
            .then(() => onRequestHistoryRef.current?.())
            .catch(() => null)
            .finally(() => {
              historyRequestInFlightRef.current = false;
            });
        }
      };

      if (timeScale?.subscribeVisibleLogicalRangeChange) {
        timeScale.subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      }

      const handleCrosshairMove = (param) => {
        if (
          volumeCandleOverlayConfigRef.current?.enabled &&
          Number.isFinite(param?.point?.x)
        ) {
          const packedItem = findPackedVolumeCandle(
            volumeCandleLayoutRef.current,
            param.point.x,
          );
          if (packedItem?.candle) {
            updateOhlcBox(ohlcRef.current, packedItem.candle, assetType, {
              explainChange: explainBarChange,
            });
            updateVolumeCandleTimeLabel({
              candle: packedItem.candle,
              x: packedItem.x,
            });
            return;
          }
        }

        hideVolumeCandleTimeLabel();
        if (!param?.time) {
          updateOhlcBox(ohlcRef.current, latestCandleRef.current, assetType, {
            explainChange: explainBarChange,
          });
          return;
        }

        let candle = candleLookupRef.current.get(param.time) || null;
        if (
          !candle &&
          typeof param.seriesData?.get === "function" &&
          seriesRef.current
        ) {
          const hovered = param.seriesData.get(seriesRef.current);
          if (
            hovered &&
            Number.isFinite(hovered.open) &&
            Number.isFinite(hovered.high) &&
            Number.isFinite(hovered.low) &&
            Number.isFinite(hovered.close)
          ) {
            candle = hovered;
          }
        }

        if (!candle) {
          candle = latestCandleRef.current;
        }
        updateOhlcBox(ohlcRef.current, candle, assetType, {
          explainChange: explainBarChange,
        });
      };

      if (chart.subscribeCrosshairMove) {
        chart.subscribeCrosshairMove(handleCrosshairMove);
      }

      const chartRoot = chartRootRef.current;
      const localPointForEvent = (event) => {
        const bounds = chartRoot?.getBoundingClientRect?.();
        if (!bounds) return null;
        const x = Number(event?.clientX) - bounds.left;
        const y = Number(event?.clientY) - bounds.top;
        const plotWidth = Number(chart.timeScale?.()?.width?.()) || bounds.width;
        if (
          !Number.isFinite(x) ||
          !Number.isFinite(y) ||
          x < 0 ||
          y < 0 ||
          x > plotWidth ||
          y > bounds.height
        ) {
          return null;
        }
        return { x, y, width: bounds.width, height: bounds.height };
      };
      const geometryForInteraction = (draftOverlay = null) => {
        const root = chartRootRef.current;
        const activeSeries = seriesRef.current;
        if (!root || !activeSeries) return [];
        return buildEditableOverlayGeometry({
          chart,
          series: activeSeries,
          overlays: agenticOverlayConfigRef.current?.editableOverlays,
          priceTimes: agenticOverlayConfigRef.current?.priceTimes,
          width: root.clientWidth,
          height: root.clientHeight,
          draftOverlay,
        });
      };
      const anchorForEvent = (event) => {
        const point = localPointForEvent(event);
        if (!point || !seriesRef.current) return null;
        const anchor = drawingAnchorForChartPoint({
          chart,
          series: seriesRef.current,
          priceTimes: agenticOverlayConfigRef.current?.priceTimes,
          x: point.x,
          y: point.y,
        });
        return anchor ? { ...point, anchor } : null;
      };
      const stopChartGesture = (event) => {
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };
      const handleOverlayPointerDown = (event) => {
        const options = overlayInteractionOptionsRef.current;
        if (
          !options?.editable ||
          options.busy ||
          overlayCommitPendingRef.current ||
          event.button !== 0
        ) {
          return;
        }
        if (
          event.target instanceof Element &&
          event.target.closest("[data-chart-drawing-toolbar]")
        ) {
          return;
        }

        const point = anchorForEvent(event);
        if (!point) return;
        const drawingMode = options.drawingMode;
        if (drawingMode?.kind) {
          const kind = String(drawingMode.kind).toLowerCase();
          if (!CHART_DRAWING_CREATION_KINDS.includes(kind)) return;
          const baseOverlay = {
            ...drawingMode,
            id: String(drawingMode.id),
            kind,
          };
          const freehand = FREEHAND_DRAWING_KINDS.includes(kind);
          const draftOverlay =
            kind === "horizontal_line"
              ? { ...baseOverlay, price: point.anchor.price }
              : freehand
                ? { ...baseOverlay, points: [{ ...point.anchor }] }
              : {
                  ...baseOverlay,
                  start_anchor: { ...point.anchor },
                  end_anchor: { ...point.anchor },
                };
          overlayInteractionRef.current = {
            type: "create",
            kind,
            originalOverlay: null,
            startPoint: point,
            dragStart: point.anchor,
            pathSamples: freehand ? [point] : null,
          };
          draftAgenticOverlayRef.current = draftOverlay;
          selectEditableOverlay(draftOverlay);
          chartRoot?.focus?.({ preventScroll: true });
          setOverlayCursor("crosshair");
          scheduleTradeOverlayDraw();
          stopChartGesture(event);
          return;
        }

        const hit = hitTestEditableOverlay({
          geometry:
            editableOverlayGeometryRef.current.length > 0
              ? editableOverlayGeometryRef.current
              : geometryForInteraction(),
          x: point.x,
          y: point.y,
          selectedId: selectedAgenticOverlayRef.current?.id,
        });
        if (!hit) {
          if (
            selectedAgenticOverlayRef.current &&
            String(selectedAgenticOverlayRef.current.id) !== String(options.draftId)
          ) {
            selectEditableOverlay(null);
          }
          setOverlayCursor("");
          return;
        }

        if (
          String(hit.item.overlay?.kind || "").toLowerCase() === "trade_setup" &&
          String(hit.item.overlay?.id) !== String(options.draftId)
        ) {
          selectEditableOverlay(hit.item.overlay);
          chartRoot?.focus?.({ preventScroll: true });
          setOverlayCursor("");
          scheduleTradeOverlayDraw();
          stopChartGesture(event);
          return;
        }

        if (hit.item.overlay?.locked === true) {
          selectEditableOverlay(hit.item.overlay);
          chartRoot?.focus?.({ preventScroll: true });
          setOverlayCursor("pointer");
          scheduleTradeOverlayDraw();
          stopChartGesture(event);
          return;
        }

        overlayInteractionRef.current = {
          type: "move",
          handle: hit.handle,
          originalOverlay: { ...hit.item.overlay },
          startPoint: point,
          dragStart: point.anchor,
        };
        draftAgenticOverlayRef.current = { ...hit.item.overlay };
        selectEditableOverlay(hit.item.overlay);
        chartRoot?.focus?.({ preventScroll: true });
        setOverlayCursor("grabbing");
        scheduleTradeOverlayDraw();
        stopChartGesture(event);
      };
      const handleOverlayPointerMove = (event) => {
        const options = overlayInteractionOptionsRef.current;
        if (!options?.editable || options.busy) return;
        const interaction = overlayInteractionRef.current;
        if (!interaction) {
          const point = localPointForEvent(event);
          if (!point) {
            setOverlayCursor(options.drawingMode ? "crosshair" : "");
            return;
          }
          if (options.drawingMode) {
            setOverlayCursor("crosshair");
            return;
          }
          const hit = hitTestEditableOverlay({
            geometry: editableOverlayGeometryRef.current,
            x: point.x,
            y: point.y,
            selectedId: selectedAgenticOverlayRef.current?.id,
          });
          setOverlayCursor(
            hit?.item?.overlay?.locked === true ? "pointer" : hit ? "grab" : "",
          );
          return;
        }

        const point = anchorForEvent(event);
        if (!point) return;
        let draftOverlay = draftAgenticOverlayRef.current;
        if (interaction.type === "create") {
          if (FREEHAND_DRAWING_KINDS.includes(interaction.kind)) {
            const coalesced = event.getCoalescedEvents?.() || [];
            const sampleEvents = coalesced.length > 0
              ? [...coalesced, event]
              : [event];
            let pathSamples = interaction.pathSamples || [];
            sampleEvents.forEach((sampleEvent) => {
              const sample = anchorForEvent(sampleEvent);
              pathSamples = appendFreehandSample(pathSamples, sample, {
                maxCapturePoints: FREEHAND_MAX_CAPTURE_POINTS,
              });
            });
            interaction.pathSamples = pathSamples;
            draftOverlay = freehandOverlayFromSamples({
              overlay: draftOverlay,
              samples: pathSamples,
              tolerance: 0.6,
              maxPoints: FREEHAND_MAX_CAPTURE_POINTS,
            });
          } else {
            draftOverlay =
              interaction.kind === "horizontal_line"
                ? { ...draftOverlay, price: point.anchor.price }
                : {
                    ...draftOverlay,
                    end_anchor: { ...point.anchor },
                  };
          }
        } else {
          draftOverlay = moveEditableOverlay({
            overlay: interaction.originalOverlay,
            handle: interaction.handle,
            dragStart: interaction.dragStart,
            current: point.anchor,
            priceTimes: agenticOverlayConfigRef.current?.priceTimes,
          });
        }
        draftAgenticOverlayRef.current = draftOverlay;
        applyDraftPriceLine(draftOverlay);
        scheduleTradeOverlayDraw();
        setOverlayCursor(interaction.type === "move" ? "grabbing" : "crosshair");
        stopChartGesture(event);
      };
      const handleOverlayPointerUp = (event) => {
        const interaction = overlayInteractionRef.current;
        if (!interaction) return;
        stopChartGesture(event);
        overlayInteractionRef.current = null;
        let draftOverlay = draftAgenticOverlayRef.current;
        if (
          interaction.type === "create" &&
          FREEHAND_DRAWING_KINDS.includes(interaction.kind)
        ) {
          const finalSample = anchorForEvent(event);
          interaction.pathSamples = appendFreehandSample(
            interaction.pathSamples,
            finalSample,
            { force: true, maxCapturePoints: FREEHAND_MAX_CAPTURE_POINTS },
          );
          draftOverlay = freehandOverlayFromSamples({
            overlay: draftOverlay,
            samples: interaction.pathSamples,
          });
          draftAgenticOverlayRef.current = draftOverlay;
        }
        if (!draftOverlay) {
          clearOverlayInteraction({
            restoreOverlay: interaction.originalOverlay,
            clearSelection: interaction.type === "create",
          });
          return;
        }

        if (interaction.type === "create" && interaction.kind !== "horizontal_line") {
          const draftGeometry = geometryForInteraction(draftOverlay).find(
            (item) => String(item.id) === String(draftOverlay.id),
          );
          if (drawingGeometryLength(draftGeometry) < DRAWING_MIN_DRAG_PX) {
            clearOverlayInteraction({
              restoreOverlay: interaction.originalOverlay,
              clearSelection: interaction.type === "create",
            });
            return;
          }
        }

        if (
          interaction.type === "move" &&
          JSON.stringify(draftOverlay) ===
            JSON.stringify(interaction.originalOverlay)
        ) {
          clearOverlayInteraction();
          return;
        }

        if (String(draftOverlay.kind || "").toLowerCase() !== "trade_setup") {
          draftOverlay = { ...draftOverlay, user_confirmed: true };
          draftAgenticOverlayRef.current = draftOverlay;
        }

        selectEditableOverlay(draftOverlay);
        overlayCommitPendingRef.current = true;
        const options = overlayInteractionOptionsRef.current;
        const callback =
          interaction.type === "create" ? options?.onCreate : options?.onChange;
        let result = false;
        try {
          result = callback?.(draftOverlay) ?? false;
        } catch (_error) {
          result = false;
        }
        Promise.resolve(result)
          .then((saved) => {
            clearOverlayInteraction({
              restoreOverlay: saved === false ? interaction.originalOverlay : null,
              clearSelection: saved === false && interaction.type === "create",
            });
          })
          .catch(() => {
            clearOverlayInteraction({
              restoreOverlay: interaction.originalOverlay,
              clearSelection: interaction.type === "create",
            });
          });
      };
      const handleOverlayPointerCancel = () => {
        const interaction = overlayInteractionRef.current;
        if (!interaction) return;
        clearOverlayInteraction({
          restoreOverlay: interaction.originalOverlay,
          clearSelection: interaction.type === "create",
        });
      };
      const handleOverlayKeyDown = (event) => {
        const options = overlayInteractionOptionsRef.current;
        const key = String(event.key || "").toLowerCase();
        const clipboardChord = (event.metaKey || event.ctrlKey) && !event.altKey;
        const textEntryTarget =
          event.target instanceof Element &&
          event.target.closest("input, textarea, select, [contenteditable='true']");
        if (textEntryTarget) return;
        if (clipboardChord && (key === "z" || key === "y")) {
          if (options.busy || overlayInteractionRef.current) return;
          const action = key === "y" || event.shiftKey ? options.onRedo : options.onUndo;
          if (action) { stopChartGesture(event); void action(); }
          return;
        }
        if (clipboardChord && !textEntryTarget && key === "c") {
          if (copySelectedOverlay()) stopChartGesture(event);
          return;
        }
        if (clipboardChord && !textEntryTarget && key === "v") {
          if (pasteCopiedOverlay()) stopChartGesture(event);
          return;
        }
        if (event.key === "Escape") {
          if (
            options?.draftId &&
            String(selectedAgenticOverlayRef.current?.id) === String(options.draftId)
          ) {
            stopChartGesture(event);
            Promise.resolve(options.onDraftCancel?.()).then((cancelled) => {
              if (cancelled === false) return;
              selectEditableOverlay(null);
            });
            return;
          }
          if (overlayInteractionRef.current || options?.drawingMode) {
            stopChartGesture(event);
            handleOverlayPointerCancel();
            options?.onCancelDrawing?.();
          } else if (selectedAgenticOverlayRef.current) {
            stopChartGesture(event);
            selectEditableOverlay(null);
          }
          return;
        }
        if (
          !["Backspace", "Delete"].includes(event.key) ||
          !selectedAgenticOverlayRef.current?.id ||
          !options?.onDelete ||
          options.busy ||
          selectedAgenticOverlayRef.current?.locked === true
        ) {
          return;
        }
        stopChartGesture(event);
        const selectedId = selectedAgenticOverlayRef.current.id;
        const overlay = agenticOverlayConfigRef.current?.editableOverlays?.find(
          (row) => String(row.id) === String(selectedId),
        );
        if (!overlay) return;
        Promise.resolve(options.onDelete(overlay)).then((removed) => {
          if (removed === false) return;
          selectEditableOverlay(null);
        });
      };
      const handleChartContextMenu = (event) => {
        const options = overlayInteractionOptionsRef.current;
        if (!options?.editable || typeof options.onContextMenu !== "function") return;
        const point = anchorForEvent(event);
        if (!point) return;
        stopChartGesture(event);
        options.onContextMenu({
          x: event.clientX,
          y: event.clientY,
          price: point.anchor.price,
          time: point.anchor.time,
          visiblePriceRange: (() => {
            const series = seriesRef.current;
            const top = Number(series?.coordinateToPrice?.(12));
            const bottom = Number(
              series?.coordinateToPrice?.(Math.max(12, point.height - 12)),
            );
            if (!Number.isFinite(top) || !Number.isFinite(bottom) || top === bottom) {
              return null;
            }
            return { min: Math.min(top, bottom), max: Math.max(top, bottom) };
          })(),
        });
      };

      overlayPointerHandlersRef.current = {
        pointerDown: handleOverlayPointerDown,
        keyDown: handleOverlayKeyDown,
        contextMenu: handleChartContextMenu,
      };
      window.addEventListener("pointermove", handleOverlayPointerMove, true);
      window.addEventListener("pointerup", handleOverlayPointerUp, true);
      window.addEventListener("pointercancel", handleOverlayPointerCancel, true);

      const unsubscribeSharedRange = sharedRangeBus?.subscribe?.(
        (range, sourceId, context) => {
          if (sourceId === syncIdRef.current) return;
          if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
            return;
          }
          if (context?.scale && context.scale !== scale) return;
          const synchronizedRange =
            context?.pointCount
              ? rebaseLogicalRangeForSeriesSwitch({
                  range,
                  previousPointCount: context?.pointCount,
                  nextPointCount: pointCountRef.current,
                })
              : range;
          const current = chart.timeScale()?.getVisibleLogicalRange?.();
          if (logicalRangesClose(current, synchronizedRange)) return;
          scheduleRangeApply({
            chart,
            range: synchronizedRange,
            suppressRangeSyncRef,
          });
        }
      );

      if (typeof ResizeObserver !== "undefined") {
        resizeObserverRef.current = new ResizeObserver(() => {
          if (chartResizeFrameRef.current != null) {
            window.cancelAnimationFrame(chartResizeFrameRef.current);
          }
          chartResizeFrameRef.current = window.requestAnimationFrame(() => {
            chartResizeFrameRef.current = null;
            const bounds = container.getBoundingClientRect();
            const width = Math.max(bounds.width || 0, 320);
            const height = Math.max(bounds.height || 220, 1);
            chart.resize(width, height, true);
            scheduleTradeOverlayDraw();
            syncLivePriceLabel();
          });
        });
        resizeObserverRef.current.observe(container);
      }

      chartHandleRef.current = {
        zoom: (factor) => {
          const activeChart = chartRef.current;
          if (!activeChart) return;
          const range =
            activeChart.timeScale()?.getVisibleLogicalRange?.() ||
            getInitialLogicalRange(pointCountRef.current, scale);
          const nextRange = zoomLogicalRange(range, factor);
          if (!nextRange) return;
          scheduleRangeApply({
            chart: activeChart,
            range: nextRange,
            suppressRangeSyncRef,
          });
          publishSharedRange(nextRange);
        },
        getVisibleLogicalRange: () =>
          chartRef.current?.timeScale()?.getVisibleLogicalRange?.() || null,
        setVisibleLogicalRange: (range) => {
          if (!chartRef.current || !range) return;
          scheduleRangeApply({
            chart: chartRef.current,
            range,
            suppressRangeSyncRef,
          });
          publishSharedRange(range);
        },
        resetPriceScale: () =>
          resetChartPriceScale({
            chart: chartRef.current,
            series: seriesRef.current,
            requestFrame: window.requestAnimationFrame.bind(window),
            onReset: scheduleTradeOverlayDraw,
          }),
        getScreenshotCanvas: () => {
          const baseCanvas = resolveChartScreenshotCanvas(
            chartRef.current,
            containerRef.current
          );
          const overlayCanvas = tradeOverlayCanvasRef.current;
          if (!baseCanvas || !overlayCanvas) {
            return baseCanvas;
          }

          const composite = document.createElement("canvas");
          composite.width = baseCanvas.width;
          composite.height = baseCanvas.height;
          const context = composite.getContext("2d");
          if (!context) {
            return baseCanvas;
          }

          context.drawImage(baseCanvas, 0, 0);
          context.drawImage(
            overlayCanvas,
            0,
            0,
            overlayCanvas.width,
            overlayCanvas.height,
            0,
            0,
            composite.width,
            composite.height
          );
          return composite;
        },
        subscribePriceClick: (callback) => {
          const activeChart = chartRef.current;
          if (!activeChart?.subscribeClick || typeof callback !== "function") {
            return () => {};
          }
          const handler = (param) => {
            const y = Number(param?.point?.y);
            const activeSeries = seriesRef.current;
            if (!Number.isFinite(y) || !activeSeries?.coordinateToPrice) return;
            const price = Number(activeSeries.coordinateToPrice(y));
            if (!Number.isFinite(price)) return;
            callback(price, param);
          };
          activeChart.subscribeClick(handler);
          return () => activeChart.unsubscribeClick?.(handler);
        },
      };

      if (typeof onChartReady === "function") {
        onChartReady(chartHandleRef.current);
      }

      return () => {
        if (
          overlayPointerHandlersRef.current.pointerDown ===
          handleOverlayPointerDown
        ) {
          overlayPointerHandlersRef.current = {
            pointerDown: null,
            keyDown: null,
          };
        }
        window.removeEventListener("pointermove", handleOverlayPointerMove, true);
        window.removeEventListener("pointerup", handleOverlayPointerUp, true);
        window.removeEventListener(
          "pointercancel",
          handleOverlayPointerCancel,
          true,
        );
        if (timeScale?.unsubscribeVisibleLogicalRangeChange) {
          timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
        }
        if (chart.unsubscribeCrosshairMove) {
          chart.unsubscribeCrosshairMove(handleCrosshairMove);
        }
        if (typeof unsubscribeSharedRange === "function") {
          unsubscribeSharedRange();
        }
        removeTrackpadZoomAmplifier();
        if (
          tradeOverlayFrameRef.current != null &&
          typeof window !== "undefined"
        ) {
          window.cancelAnimationFrame(tradeOverlayFrameRef.current);
          tradeOverlayFrameRef.current = null;
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        if (chartResizeFrameRef.current != null) {
          window.cancelAnimationFrame(chartResizeFrameRef.current);
          chartResizeFrameRef.current = null;
        }
        if (attributionObserverRef.current) {
          attributionObserverRef.current.disconnect();
          attributionObserverRef.current = null;
        }
        if (typeof onChartReady === "function") {
          onChartReady(null);
        }
        clearIndicatorSeries(chart);
        priceLineRefsRef.current = [];
        seriesKindRef.current = null;
        seriesRef.current = null;
        latestCandleRef.current = null;
        provisionalLiveCandleRef.current = null;
        tradeOverlayDrawRef.current = () => {};
        tradeOverlayConfigRef.current = {
          segments: [],
          markers: [],
          priceLines: [],
        };
        agenticOverlayConfigRef.current = {
          priceLines: [],
          zones: [],
          editableOverlays: [],
          priceTimes: [],
        };
        agenticPriceLineRefsRef.current = new Map();
        editableOverlayGeometryRef.current = [];
        syncEditableOverlayGeometryMetadata([]);
        overlayInteractionRef.current = null;
        overlayCommitPendingRef.current = false;
        draftAgenticOverlayRef.current = null;
        sessionOverlayConfigRef.current = SESSION_OVERLAY_EMPTY;
        volumeCandleOverlayConfigRef.current = {
          enabled: false,
          candles: [],
          referenceVolume: null,
        };
        volumeCandleLayoutRef.current = null;
        syncVolumeCandleLayoutMetadata(null);
        previousChartIdentityRef.current = null;
        previousRangeCompatibilityRef.current = null;
        previousSeriesMetaRef.current = {
          firstTime: null,
          pointCount: 0,
        };
        previousPriceSeriesDataRef.current = {
          kind: null,
          candleData: [],
          lineData: [],
        };
        previousIndicatorSeriesConfigsRef.current = [];
        previousLiveUpdateContextRef.current = null;
        previousDesiredRangeApplicationRef.current = null;
        clearOverlayCanvas(tradeOverlayCanvasRef.current);
        hideLivePriceLabel(livePriceLabelRef.current);
        hideVolumeCandleTimeLabel();
        chartHandleRef.current = null;
        chartRef.current?.remove();
        chartRef.current = null;
      };
    }, [library, isChartVisible, isMobile, onChartReady, scale, assetType, sharedRangeBus, idx, canvasId, explainBarChange]);

    useEffect(() => {
      if (!chartRef.current) return;
      chartRef.current.applyOptions(
        buildChartOptions({
          library,
          scale,
          assetType,
          isMobile,
        })
      );
      chartRef.current.applyOptions({ attributionLogo: { visible: false } });
    }, [library, scale, assetType, isMobile]);

    useEffect(() => {
      if (!chartRef.current) return;
      const viewOptions = buildChartOptions({
        library,
        scale,
        assetType,
        isMobile,
        chartViewType,
      });
      chartRef.current.applyOptions({
        grid: {
          vertLines: viewOptions.grid.vertLines,
        },
        crosshair: {
          vertLine: viewOptions.crosshair.vertLine,
        },
        timeScale: {
          tickMarkFormatter: viewOptions.timeScale.tickMarkFormatter,
        },
      });
    }, [library, scale, assetType, isMobile, chartViewType]);

    useEffect(() => {
      if (!chartRef.current || !library || priceSeries.candleData.length === 0) {
        latestCandleRef.current = null;
        if (
          seriesRef.current &&
          typeof seriesRef.current.setMarkers === "function"
        ) {
          seriesRef.current.setMarkers([]);
        }
        if (seriesRef.current) {
          priceLineRefsRef.current.forEach((priceLine) => {
            try {
              seriesRef.current.removePriceLine?.(priceLine);
            } catch (_error) {}
          });
        }
        clearIndicatorSeries();
        priceLineRefsRef.current = [];
        tradeOverlayConfigRef.current = {
          segments: [],
          markers: [],
          priceLines: [],
        };
        agenticOverlayConfigRef.current = {
          priceLines: [],
          zones: [],
          editableOverlays: [],
          priceTimes: [],
        };
        agenticPriceLineRefsRef.current = new Map();
        editableOverlayGeometryRef.current = [];
        syncEditableOverlayGeometryMetadata([]);
        overlayInteractionRef.current = null;
        overlayCommitPendingRef.current = false;
        draftAgenticOverlayRef.current = null;
        sessionOverlayConfigRef.current = SESSION_OVERLAY_EMPTY;
        volumeCandleOverlayConfigRef.current = {
          enabled: false,
          candles: [],
          referenceVolume: null,
        };
        volumeCandleLayoutRef.current = null;
        syncVolumeCandleLayoutMetadata(null);
        previousChartIdentityRef.current = null;
        previousRangeCompatibilityRef.current = null;
        previousSeriesMetaRef.current = {
          firstTime: null,
          pointCount: 0,
        };
        previousPriceSeriesDataRef.current = {
          kind: null,
          candleData: [],
          lineData: [],
        };
        previousIndicatorSeriesConfigsRef.current = [];
        previousLiveUpdateContextRef.current = null;
        previousDesiredRangeApplicationRef.current = null;
        tradeOverlayDrawRef.current = () => {
          clearOverlayCanvas(tradeOverlayCanvasRef.current);
        };
        resetOhlcBox(ohlcRef.current);
        hideLivePriceLabel(livePriceLabelRef.current);
        hideVolumeCandleTimeLabel();
        scheduleTradeOverlayDraw();
        return;
      }

      const chart = chartRef.current;
      const palette = getPalette();
      const currentPriceLineOptions = buildCurrentPriceLineOptions({
        library,
        latestCandle:
          priceSeries.candleData[priceSeries.candleData.length - 1] || null,
        palette,
      });
      const desiredSeriesKind =
        chartViewType === "line" ? "line" : "candlestick";
      const volumeCandleOverlay = buildVolumeCandleOverlayConfig({
        candleData: priceSeries.candleData,
        chartViewType,
        identity: selectedTickerId,
        palette,
        scale,
      });
      volumeCandleOverlayConfigRef.current = volumeCandleOverlay;
      const volumeCandleAutoscaleInfoProvider = (baseImplementation) => {
        const autoscaleInfo = typeof baseImplementation === "function"
          ? baseImplementation()
          : null;
        const overlay = volumeCandleOverlayConfigRef.current;
        if (!overlay?.enabled) return autoscaleInfo;

        const timeScale = chart.timeScale?.();
        const layout = resolveVolumeCandleLayout({
          chart,
          overlay,
          width: timeScale?.width?.(),
          pixelRatio:
            typeof window === "undefined" ? 1 : window.devicePixelRatio,
        });
        return extendAutoscaleInfoWithVolumeCandleLayout({
          autoscaleInfo,
          layout,
        });
      };
      // Chart style is presentation-only. Candles, volume candles, and line
      // must share the same logical viewport for a ticker and interval.
      const chartIdentityKey = buildChartRangeIdentityKey({
        selectedTickerId,
        scale,
        assetType,
      });
      const rangeCompatibilityKey = [
        scale,
        chartViewType,
        assetType || "",
      ].join("|");
      const previousSeriesMeta = previousSeriesMetaRef.current || {};
      const previousPointCount = previousSeriesMeta.pointCount || 0;
      const nextFirstTime = priceSeries.times[0] ?? null;
      const hadPriorSeries = seriesRef.current != null;
      const desiredDisplayRange =
        agenticChartState?.display_range || agenticChartState?.displayRange || null;
      const desiredLogicalRange = getDesiredLogicalRange({
        displayRange: desiredDisplayRange,
        pointCount: priceSeries.times.length,
        times: priceSeries.times,
        rawScale: scale,
        timeZone: userTimezone,
      });
      const desiredRangeApplicationKey = desiredLogicalRange
        ? [
            chartIdentityKey,
            JSON.stringify(desiredDisplayRange || {}),
          ].join("|")
        : null;
      const shouldPreserveRange =
        hadPriorSeries && previousChartIdentityRef.current === chartIdentityKey;
      const shouldPreserveCalendarRange =
        hadPriorSeries &&
        previousRangeCompatibilityRef.current === rangeCompatibilityKey;
      const shouldApplyDesiredRange =
        desiredLogicalRange &&
        (!hadPriorSeries ||
          previousChartIdentityRef.current !== chartIdentityKey ||
          previousDesiredRangeApplicationRef.current !== desiredRangeApplicationKey);
      const isCompatibleSeriesSwitch =
        shouldPreserveCalendarRange &&
        previousChartIdentityRef.current !== chartIdentityKey;
      const preMutationLogicalRange = shouldPreserveRange
        ? normalizeLogicalRange(
            chart.timeScale()?.getVisibleLogicalRange?.() || null
          )
        : null;
      const preMutationTimeRange = shouldPreserveCalendarRange
        ? normalizeTimeRange(chart.timeScale()?.getVisibleRange?.() || null)
        : null;
      const shouldRestoreSharedRange = shouldPreserveRange || !hadPriorSeries;
      const preMutationSharedRange = shouldRestoreSharedRange
        ? normalizeLogicalRange(sharedRangeBus?.getRange?.() || null)
        : null;
      const sharedRangeSnapshot =
        shouldRestoreSharedRange && sharedRangeBus?.getRangeSnapshot
          ? sharedRangeBus.getRangeSnapshot()
          : null;
      const restoredSharedRange = restoreLogicalRangeFromSharedSnapshot({
        snapshot: sharedRangeSnapshot,
        fallbackRange: preMutationSharedRange,
        nextPointCount: priceSeries.times.length,
        nextScale: scale,
      });
      const restoredTransferredRange =
        rangeTransfer?.range && rangeTransfer.scale === scale
          ? rebaseLogicalRangeForSeriesSwitch({
              range: rangeTransfer.range,
              previousPointCount: rangeTransfer.pointCount,
              nextPointCount: priceSeries.times.length,
            })
          : null;
      const firstTimeChanged =
        shouldPreserveRange &&
        Number.isFinite(Number(previousSeriesMeta.firstTime)) &&
        Number.isFinite(Number(nextFirstTime)) &&
        Number(previousSeriesMeta.firstTime) !== Number(nextFirstTime);
      const indicatorSeriesConfigs = showIndicators
        ? buildIndicatorSeriesConfigs({
            agenticChartState,
            selectedTickerId,
            candleData: priceSeries.candleData,
            scale,
          })
        : [];
      const agenticOverlay = showAgenticOverlays
        ? buildAgenticOverlayConfig({
            agenticChartState,
            compactLabels: compactAgenticLabels,
            selectedTickerId,
            priceTimes: priceSeries.times,
          })
        : {
            priceLines: [],
            zones: [],
            editableOverlays: [],
            priceTimes: priceSeries.times,
          };
      const liveUpdateContext = [
        chartIdentityKey,
        // Durable chart live updates wrap the unchanged message metadata in a
        // new object so they can attach the latest bars. Compare only the
        // message fields that affect trade geometry; object identity would
        // force setData and reapply a stale viewport on every WebSocket tick.
        tradeOverlayContextKeyForMessage(messageThread),
        tradeOverlayKey,
        // Active simulated trades receive a new live mark on every quote.
        // That mark changes canvas P&L presentation, not series structure.
        // Keep semantic edits and lifecycle transitions on the full rebuild
        // path while allowing quote-only changes to update in place.
        agenticOverlayStructureKeyForState(agenticChartState, agenticOverlayKey),
        // DurableChartDock rebuilds the effective layer object while preserving
        // this semantic key. Comparing the object identity here would turn a
        // sidebar-only rerender into a full setData cycle and reset the viewport.
        compactAgenticLabels,
        explainBarChange,
        showIndicators,
        showAgenticOverlays,
        chartConfigKey,
        extendedHoursQuote,
        userTimezone,
      ];
      const previousLiveUpdateContext = previousLiveUpdateContextRef.current;
      const sameLiveUpdateContext =
        Array.isArray(previousLiveUpdateContext) &&
        previousLiveUpdateContext.length === liveUpdateContext.length &&
        liveUpdateContext.every(
          (value, index) => Object.is(value, previousLiveUpdateContext[index])
        );
      const currentSeriesData =
        desiredSeriesKind === "line"
          ? priceSeries.lineData
          : priceSeries.candleData;
      const previousSeriesData =
        desiredSeriesKind === "line"
          ? previousPriceSeriesDataRef.current?.lineData
          : previousPriceSeriesDataRef.current?.candleData;
      const hasAppendedProvisionalCandle = provisionalCandleExtendsSeries(
        provisionalLiveCandleRef.current,
        currentSeriesData,
      );
      const liveUpdatePoint = latestPointOnlyUpdateForDataChange({
        kind: desiredSeriesKind === "line" ? "line" : "candlestick",
        previousData: previousSeriesData || [],
        nextData: currentSeriesData,
      });
      const previousIndicatorConfigs =
        previousIndicatorSeriesConfigsRef.current || [];
      const indicatorsCanUpdateInPlace =
        indicatorSeriesConfigs.length === previousIndicatorConfigs.length &&
        indicatorSeriesConfigs.length === indicatorSeriesRefsRef.current.length &&
        indicatorSeriesConfigs.every(
          (config, index) => config.id === previousIndicatorConfigs[index]?.id
        );
      const canUseLiveUpdateFastPath =
        seriesRef.current &&
        seriesKindRef.current === desiredSeriesKind &&
        previousChartIdentityRef.current === chartIdentityKey &&
        sameLiveUpdateContext &&
        !hasAppendedProvisionalCandle &&
        liveUpdatePoint &&
        indicatorsCanUpdateInPlace;

      if (canUseLiveUpdateFastPath) {
        seriesRef.current.update?.(liveUpdatePoint);
        candleLookupRef.current.set(
          priceSeries.candleData[priceSeries.candleData.length - 1]?.time,
          priceSeries.candleData[priceSeries.candleData.length - 1]
        );
        latestCandleRef.current =
          priceSeries.candleData[priceSeries.candleData.length - 1] || null;
        indicatorSeriesConfigs.forEach((config, index) => {
          applySeriesData({
            series: indicatorSeriesRefsRef.current[index],
            kind: "line",
            previousData: previousIndicatorConfigs[index]?.data || [],
            nextData: config.data,
          });
        });
        previousIndicatorSeriesConfigsRef.current = indicatorSeriesConfigs;
        previousSeriesMetaRef.current = {
          firstTime: Number.isFinite(Number(nextFirstTime))
            ? Number(nextFirstTime)
            : null,
          pointCount: priceSeries.times.length,
        };
        previousPriceSeriesDataRef.current = {
          kind: desiredSeriesKind,
          candleData: priceSeries.candleData,
          lineData: priceSeries.lineData,
        };
        agenticOverlayConfigRef.current = agenticOverlay;
        updateOhlcBox(ohlcRef.current, latestCandleRef.current, assetType, {
          explainChange: explainBarChange,
        });
        syncLivePriceLine();
        syncLivePriceLabel();
        scheduleTradeOverlayDraw();
        return;
      }

      // setData can emit transient range changes while Lightweight Charts
      // rebuilds its time scale. Do not let those replace the user's pan state.
      suppressRangeSyncRef.current = true;
      clearIndicatorSeries(chart);
      candleLookupRef.current = new Map(
        priceSeries.candleData.map((entry) => [entry.time, entry])
      );
      latestCandleRef.current =
        priceSeries.candleData[priceSeries.candleData.length - 1] || null;

      // If older history is prepended, logical indexes shift. Preserve the
      // calendar window for that swap. Compatible dataset switches, such as
      // ticker tabs on the same scale/view, also preserve the calendar window
      // so the user's May/June view does not auto-fit to a different ticker's
      // full history.
      const preservedTimeRange =
        !restoredTransferredRange &&
        !shouldApplyDesiredRange &&
        // Calendar ranges contain only real bars. Restoring one while the
        // viewport extends beyond the candles would trim its empty margins
        // on the HTTP history expansion. Rebase that logical range instead.
        !(preMutationLogicalRange?.from < 0 ||
          preMutationLogicalRange?.to > previousPointCount - 1) &&
        preMutationTimeRange &&
        (firstTimeChanged ||
          (isCompatibleSeriesSwitch &&
            timeRangeOverlapsSeries({
              range: preMutationTimeRange,
              times: priceSeries.times,
            })))
        ? preMutationTimeRange
        : null;

      let nextSeries = seriesRef.current;
      const canReuseSeries =
        nextSeries && seriesKindRef.current === desiredSeriesKind;
      const previousPriceSeriesData =
        canReuseSeries && !hasAppendedProvisionalCandle
          ? previousPriceSeriesDataRef.current
          : null;

      if (!canReuseSeries && seriesRef.current) {
        chart.removeSeries(seriesRef.current);
        seriesRef.current = null;
        seriesKindRef.current = null;
        priceLineRefsRef.current = [];
        livePriceLineRef.current = null;
        nextSeries = null;
      }

      if (desiredSeriesKind === "line") {
        if (!nextSeries) {
          nextSeries = chart.addLineSeries({
            color: palette.line,
            lineWidth: 2,
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            ...currentPriceLineOptions,
          });
          seriesKindRef.current = desiredSeriesKind;
        } else {
          nextSeries.applyOptions?.({
            color: palette.line,
            lineWidth: 2,
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            ...currentPriceLineOptions,
          });
        }
        applySeriesData({
          series: nextSeries,
          kind: "line",
          previousData: previousPriceSeriesData?.lineData || [],
          nextData: priceSeries.lineData,
        });
      } else {
        const bodyColor =
          chartViewType === VOLUME_CANDLE_VIEW
            ? "rgba(0, 0, 0, 0)"
            : null;
        if (!nextSeries) {
          nextSeries = chart.addCandlestickSeries({
            upColor: bodyColor || palette.up,
            downColor: bodyColor || palette.down,
            borderUpColor: bodyColor || palette.up,
            borderDownColor: bodyColor || palette.down,
            wickUpColor: bodyColor || palette.up,
            wickDownColor: bodyColor || palette.down,
            lastValueVisible: false,
            ...currentPriceLineOptions,
            autoscaleInfoProvider: volumeCandleAutoscaleInfoProvider,
          });
          seriesKindRef.current = desiredSeriesKind;
        } else {
          nextSeries.applyOptions?.({
            upColor: bodyColor || palette.up,
            downColor: bodyColor || palette.down,
            borderUpColor: bodyColor || palette.up,
            borderDownColor: bodyColor || palette.down,
            wickUpColor: bodyColor || palette.up,
            wickDownColor: bodyColor || palette.down,
            lastValueVisible: false,
            ...currentPriceLineOptions,
            autoscaleInfoProvider: volumeCandleAutoscaleInfoProvider,
          });
        }
        applySeriesData({
          series: nextSeries,
          kind: "candlestick",
          previousData: previousPriceSeriesData?.candleData || [],
          nextData: priceSeries.candleData,
        });
      }

      const tradeOverlay = buildTradeOverlayConfig({
        messageThread,
        selectedTickerId,
        assetType,
        tickerPrices,
        scale,
        priceTimes: priceSeries.times,
      });
      const sessionOverlay = buildSessionOverlayConfig({
        candleData: priceSeries.candleData,
        assetType,
        scale,
      });
      const effectivePriceMarkers =
        Array.isArray(priceMarkers) && priceMarkers.length > 0
          ? priceMarkers
          : extendedHoursQuote?.referencePrice != null
            ? [{
                role: "regular_close",
                price: extendedHoursQuote.referencePrice,
              }]
            : [];
      const marketReferencePriceLines = buildMarketReferencePriceLines({
        priceMarkers: effectivePriceMarkers,
        livePrice: livePriceQuote?.price ?? extendedHoursQuote?.price,
        palette,
      });

      tradeOverlayConfigRef.current = tradeOverlay;
      agenticOverlayConfigRef.current = agenticOverlay;
      sessionOverlayConfigRef.current = sessionOverlay;

      if (nextSeries.setMarkers) {
        nextSeries.setMarkers(tradeOverlay.markers);
      }
      indicatorSeriesConfigs.forEach((indicatorConfig) => {
        const indicatorSeries = chart.addLineSeries?.({
          color: indicatorConfig.color,
          lineWidth: indicatorConfig.lineWidth || 2,
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        if (!indicatorSeries) return;
        indicatorSeries.setData(indicatorConfig.data);
        indicatorSeriesRefsRef.current.push(indicatorSeries);
      });
      priceLineRefsRef.current.forEach((priceLine) => {
        try {
          nextSeries.removePriceLine?.(priceLine);
        } catch (_error) {}
      });
      priceLineRefsRef.current = [];
      agenticPriceLineRefsRef.current = new Map();
      const priceLineConfigs = [
        ...tradeOverlay.priceLines,
        ...agenticOverlay.priceLines,
        ...marketReferencePriceLines,
      ];
      priceLineConfigs.forEach((priceLineConfig) => {
        if (
          !Number.isFinite(priceLineConfig?.value) ||
          typeof nextSeries.createPriceLine !== "function"
        ) {
          return;
        }
        const priceLine = nextSeries.createPriceLine({
          price: priceLineConfig.value,
          color: priceLineConfig.color,
          lineWidth: priceLineConfig.lineWidth || 2,
          lineStyle: priceLineConfig.dashed
            ? library?.LineStyle?.Dashed ?? 2
            : library?.LineStyle?.Solid ?? 0,
          axisLabelVisible: priceLineConfig.axisLabelVisible !== false,
          title: priceLineConfig.label || "",
          lineVisible: true,
        });
        if (priceLine) {
          priceLineRefsRef.current.push(priceLine);
          if (priceLineConfig.id) {
            agenticPriceLineRefsRef.current.set(
              String(priceLineConfig.id),
              priceLine,
            );
          }
        }
      });
      applyDraftPriceLine(draftAgenticOverlayRef.current);

      seriesRef.current = nextSeries;
      tradeOverlayDrawRef.current = () => {
        const {
          volumeCandleLayout,
          editableOverlayGeometry,
        } = drawOverlayCanvas({
          chart,
          series: nextSeries,
          sessionOverlay: sessionOverlayConfigRef.current,
          volumeCandleOverlay: volumeCandleOverlayConfigRef.current,
          tradeOverlay: tradeOverlayConfigRef.current,
          agenticOverlay: agenticOverlayConfigRef.current,
          selectedAgenticOverlayId:
            selectedAgenticOverlayRef.current?.id || null,
          draftAgenticOverlay: draftAgenticOverlayRef.current,
          canvas: tradeOverlayCanvasRef.current,
        });
        volumeCandleLayoutRef.current = volumeCandleLayout;
        editableOverlayGeometryRef.current = editableOverlayGeometry;
        syncEditableOverlayGeometryMetadata(editableOverlayGeometry);
        syncVolumeCandleLayoutMetadata(volumeCandleLayout);
        if (!volumeCandleLayout) hideVolumeCandleTimeLabel();
      };

      let appliedRange = null;
      if (preservedTimeRange) {
        try {
          chart.timeScale().setVisibleRange(preservedTimeRange);
          appliedRange = normalizeLogicalRange(
            chart.timeScale()?.getVisibleLogicalRange?.() || null
          );
        } catch (_err) {
          // setVisibleRange can throw if the preserved range falls outside the
          // new dataset's bounds; fall back to logical preservation below.
          appliedRange = null;
        }
      }

      if (!appliedRange) {
        // A mounted sibling or the chart from the prior scale represents the
        // user's latest viewport. Prefer it over the message's initial display
        // range when rebuilding this chart.
        const preferredRange =
          (preferUpdatedDisplayRange && shouldApplyDesiredRange ? desiredLogicalRange : null) ||
          restoredTransferredRange ||
          restoredSharedRange ||
          (shouldApplyDesiredRange ? desiredLogicalRange : null) ||
          (shouldPreserveRange ? preMutationLogicalRange : null) ||
          getInitialLogicalRange(priceSeries.times.length, scale);
        const preferredRangeAlreadyRebased = Boolean(
          (preferUpdatedDisplayRange && shouldApplyDesiredRange) ||
          restoredTransferredRange ||
            (restoredSharedRange && sharedRangeSnapshot?.range)
        );
        const nextRange = shouldPreserveRange
          ? preserveLogicalRangeForAppend({
              range: preferredRange,
              previousPointCount,
              nextPointCount: priceSeries.times.length,
              rangeAlreadyRebased: preferredRangeAlreadyRebased,
            })
          : normalizeLogicalRange(preferredRange);
        const currentRange = normalizeLogicalRange(
          chart.timeScale()?.getVisibleLogicalRange?.() || null
        );

        if (
          nextRange &&
          !logicalRangesClose(currentRange, nextRange) &&
          chart.timeScale()?.setVisibleLogicalRange
        ) {
          chart.timeScale().setVisibleLogicalRange(nextRange);
        }
        appliedRange = nextRange;
      }

      if (appliedRange && sharedRangeBus?.setRange) {
        sharedRangeBus.setRange(appliedRange, syncIdRef.current, {
          pointCount: priceSeries.times.length,
          scale,
        });
      }
      if (appliedRange && restoredTransferredRange) {
        rangeTransfer?.onApplied?.();
      }
      syncVisibleRangeMetadata(appliedRange);
      releaseRangeSyncSuppression(suppressRangeSyncRef);

      previousChartIdentityRef.current = chartIdentityKey;
      previousRangeCompatibilityRef.current = rangeCompatibilityKey;
      previousSeriesMetaRef.current = {
        firstTime: Number.isFinite(Number(nextFirstTime))
          ? Number(nextFirstTime)
          : null,
        pointCount: priceSeries.times.length,
      };
      previousPriceSeriesDataRef.current = {
        kind: desiredSeriesKind,
        candleData: priceSeries.candleData,
        lineData: priceSeries.lineData,
      };
      previousIndicatorSeriesConfigsRef.current = indicatorSeriesConfigs;
      previousLiveUpdateContextRef.current = liveUpdateContext;
      previousDesiredRangeApplicationRef.current = desiredRangeApplicationKey;

      if (!hasRendered) {
        setHasRendered(true);
      }

      updateOhlcBox(ohlcRef.current, latestCandleRef.current, assetType, {
        explainChange: explainBarChange,
      });
      syncProvisionalLiveCandle();
      syncLivePriceLine();
      syncLivePriceLabel();
      scheduleTradeOverlayDraw();
    }, [
      library,
      messageThread,
      selectedTickerId,
      chartViewType,
      priceSeries,
      scale,
      assetType,
      tradeOverlayKey,
      agenticOverlayKey,
      agenticChartState,
      compactAgenticLabels,
      explainBarChange,
      showIndicators,
      showAgenticOverlays,
      chartConfigKey,
      extendedHoursQuote,
      priceMarkers,
      userTimezone,
      hasRendered,
      setHasRendered,
      sharedRangeBus,
      rangeTransfer,
      preferUpdatedDisplayRange,
    ]);

    useEffect(() => {
      if (typeof window === "undefined") return undefined;

      syncLivePriceLabel();
      const intervalId = window.setInterval(() => {
        syncLivePriceLabel();
      }, 1000);

      return () => {
        window.clearInterval(intervalId);
      };
    }, [scale, chartViewType, selectedTickerId, isChartVisible, assetType, extendedHoursQuote]);

    useEffect(() => {
      syncProvisionalLiveCandle();
      syncLivePriceLine();
      syncLivePriceLabel();
    }, [
      extendedHoursQuote,
      isChartVisible,
      livePriceQuote,
      provisionalLiveCandle,
      selectedTickerId,
    ]);

    const deleteSelectedOverlay = () => {
      const selectedId = selectedAgenticOverlayRef.current?.id;
      if (
        !selectedId ||
        selectedAgenticOverlayRef.current?.locked === true ||
        typeof onAgenticOverlayDelete !== "function"
      ) return false;
      const overlay =
        agenticOverlayConfigRef.current?.editableOverlays?.find(
          (row) => String(row?.id) === String(selectedId),
        ) ||
        (Array.isArray(agenticChartState?.overlays)
          ? agenticChartState.overlays.find(
              (row) => String(row?.id) === String(selectedId),
            )
          : null);
      if (!overlay) return false;
      return Promise.resolve(onAgenticOverlayDelete(overlay)).then((removed) => {
        if (removed === false) return false;
        selectEditableOverlay(null);
        return true;
      });
    };

    const hideSelectedOverlay = () => {
      const selected = selectedAgenticOverlayRef.current;
      if (!selected?.id || typeof onAgenticOverlayHide !== "function") {
        return false;
      }
      return Promise.resolve(onAgenticOverlayHide(selected)).then((hidden) => {
        if (hidden === false) return false;
        selectEditableOverlay(null);
        return true;
      });
    };

    const changeSelectedOverlay = (patch) => {
      const selected = selectedAgenticOverlayRef.current;
      if (!selected?.id || typeof onAgenticOverlayChange !== "function") {
        return false;
      }
      const unlocking =
        selected.locked === true &&
        Object.keys(patch || {}).length === 1 &&
        patch?.locked === false;
      if (selected.locked === true && !unlocking) return false;

      const next = {
        ...selected,
        ...(patch || {}),
        user_confirmed: true,
      };
      selectEditableOverlay(next);
      return Promise.resolve(onAgenticOverlayChange(next))
        .then((saved) => {
          if (saved === false) {
            selectEditableOverlay(selected);
            return false;
          }
          return true;
        })
        .catch(() => {
          selectEditableOverlay(selected);
          return false;
        });
    };

    const confirmedSelectedOverlay = async () => {
      const selected = selectedAgenticOverlayRef.current;
      if (!selected?.id) return null;
      if (selected.user_confirmed === true || String(selected.id).startsWith("manual:")) {
        return selected;
      }
      const confirmed = { ...selected, user_confirmed: true };
      const saved = await changeSelectedOverlay({ user_confirmed: true });
      return saved === false ? null : confirmed;
    };

    const askAboutSelectedOverlay = () => {
      const selected = selectedAgenticOverlayRef.current;
      if (!selected?.id || typeof onAgenticOverlayAsk !== "function") return false;
      return onAgenticOverlayAsk(selected);
    };

    const useSelectedOverlayInPlan = async () => {
      if (typeof onAgenticOverlayUseInPlan !== "function") return false;
      const overlay = await confirmedSelectedOverlay();
      if (!overlay) return false;
      return onAgenticOverlayUseInPlan(overlay);
    };

    const monitorSelectedOverlay = () => {
      const selected = selectedAgenticOverlayRef.current;
      if (!selected?.id || typeof onAgenticOverlayCreateMonitor !== "function") {
        return false;
      }
      return onAgenticOverlayCreateMonitor(selected);
    };

    const cancelDraftOverlay = () => {
      const selectedId = selectedAgenticOverlayRef.current?.id;
      if (!selectedId || String(selectedId) !== String(draftAgenticOverlayId)) return;
      const result = onAgenticOverlayDraftCancel?.();
      Promise.resolve(result).then((cancelled) => {
        if (cancelled === false) return;
        selectEditableOverlay(null);
      });
    };

    const submitDraftOverlay = () => {
      const selectedId = selectedAgenticOverlayRef.current?.id;
      if (!selectedId || String(selectedId) !== String(draftAgenticOverlayId)) return;
      const overlay =
        String(draftAgenticOverlayRef.current?.id) === String(selectedId)
          ? draftAgenticOverlayRef.current
          : agenticOverlayConfigRef.current?.editableOverlays?.find(
              (row) => String(row?.id) === String(selectedId),
            );
      if (!overlay) return;
      Promise.resolve(onAgenticOverlayDraftSubmit?.(overlay)).then((submitted) => {
        if (submitted === false) return;
        selectEditableOverlay(null);
      });
    };

    const startDraftOverlay = () => {
      const overlay = selectedAgenticOverlayRef.current;
      if (!overlay || String(overlay.kind || "").toLowerCase() !== "trade_setup") return;
      const result = onAgenticOverlayDraftStart?.(overlay);
      Promise.resolve(result).then((started) => {
        if (started === false) return;
        selectEditableOverlay(overlay);
      });
    };

    const selectedTrade =
      String(selectedAgenticOverlay?.kind || "").toLowerCase() === "trade_setup"
        ? selectedAgenticOverlay
        : null;
    const selectedTradeStatus = String(selectedTrade?.lifecycle_status || "draft").toLowerCase();
    const mobileTradeSheet = Boolean(selectedTrade && (isMobile || isNarrowViewport));
    const mobileSelectionSheet = mobileTradeSheet || Boolean(!selectedTrade && (isMobile || isNarrowViewport));
    const drawingDetailsOpen = Boolean(selectedAgenticOverlay && !selectedTrade && drawingDetailsTab);
    const dockDrawingDetails = drawingLayoutWidth >= 620 && !isMobile && !isNarrowViewport;
    const selectedTradeIsDraft =
      selectedTrade &&
      String(selectedTrade.id) === String(draftAgenticOverlayId);
    const selectedTradeMark =
      selectedTradeStatus === "closed"
        ? selectedTrade?.exit_price
        : selectedTrade?.live_price;
    const selectedTradeMetrics = selectedTrade
      ? calculateTradeMetrics({
          direction: selectedTrade.direction,
          fillPrice: selectedTrade.fill_price,
          markPrice: selectedTradeMark,
          quantity: selectedTrade.quantity,
          pointValue: selectedTrade.point_value,
          currency: selectedTrade.pnl_currency,
        })
      : null;
    const selectedDrawingPlanContext =
      !selectedTrade && typeof resolveAgenticOverlayPlanContext === "function"
        ? resolveAgenticOverlayPlanContext(selectedAgenticOverlay)
        : null;
    const selectedTradeCurrencyPnl = formatSignedTradeCurrency(
      selectedTradeMetrics?.monetaryChange,
      selectedTradeMetrics?.currency,
    );
    const selectedTradePercentPnl = selectedTradeMetrics
      ? `${selectedTradeMetrics.percentChange >= 0 ? "+" : ""}${selectedTradeMetrics.percentChange.toFixed(2)}%`
      : null;
    const selectExecutionTarget = (target) => {
      const overlay = agenticOverlayConfigRef.current?.editableOverlays?.find(
        (row) => String(row?.id) === String(target.id),
      );
      if (overlay) selectEditableOverlay(overlay);
    };
    const commitTradeQuantity = (event) => {
      if (!selectedTradeIsDraft || selectedTradeStatus === "active") return;
      const quantity = normalizeTradeQuantity(event.currentTarget.value, null);
      if (quantity == null) {
        event.currentTarget.value = formatTradeQuantity(selectedTrade.quantity);
        return;
      }
      const overlay = { ...selectedTrade, quantity };
      draftAgenticOverlayRef.current = overlay;
      selectEditableOverlay(overlay);
      Promise.resolve(onAgenticOverlayChange?.(overlay));
    };

    const resolvedStyle = useMemo(
      () => ({
        width: "100%",
        height: "100%",
        display: "block",
        position: "relative",
        ...(canvasStyle || {}),
        ...(agenticOverlayDrawingMode?.kind
          ? {
              touchAction: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }
          : isMobile
          ? {
              touchAction: "pan-y pinch-zoom",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }
          : {}),
      }),
      [agenticOverlayDrawingMode?.kind, canvasStyle, isMobile]
    );

    if (loadError) {
      return (
        <div
          id={canvasId}
          style={{
            ...resolvedStyle,
            color: getPalette().mutedText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
          }}
        >
          Chart unavailable
        </div>
      );
    }

    return (
      <div ref={drawingLayoutRef} data-chart-drawing-layout style={{ width: "100%", height: "100%", minHeight: 0, display: "flex", position: "relative" }}>
      <div
        ref={chartRootRef}
        id={canvasId || `chart-${idx}`}
        data-chart-scale={scale}
        data-chart-view={chartViewType}
        data-chart-point-count={priceSeries.times.length}
        data-chart-last-close={tickerPrices?.[tickerPrices.length - 1]?.close ?? undefined}
        data-chart-history-loading={historyLoading ? "true" : "false"}
        data-chart-history-has-more={historyHasMore ? "true" : "false"}
        data-chart-current-price-line="dotted"
        data-chart-current-price-line-width="1"
        data-chart-indicator-count={
          showIndicators && Array.isArray(agenticChartState?.indicators)
            ? agenticChartState.indicators.length
            : 0
        }
        data-chart-agentic-overlay-count={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.length
            : 0
        }
        data-chart-trade-setup-count={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.filter(
                (overlay) => String(overlay?.kind || "").toLowerCase() === "trade_setup",
              ).length
            : 0
        }
        data-chart-trade-pending-count={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.filter(
                (overlay) =>
                  String(overlay?.kind || "").toLowerCase() === "trade_setup" &&
                  String(overlay?.lifecycle_status || "").toLowerCase() === "pending_entry",
              ).length
            : 0
        }
        data-chart-trade-active-count={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.filter(
                (overlay) =>
                  String(overlay?.kind || "").toLowerCase() === "trade_setup" &&
                  String(overlay?.lifecycle_status || "").toLowerCase() === "active",
              ).length
            : 0
        }
        data-chart-trade-closed-count={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.filter(
                (overlay) =>
                  String(overlay?.kind || "").toLowerCase() === "trade_setup" &&
                  String(overlay?.lifecycle_status || "").toLowerCase() === "closed",
              ).length
            : 0
        }
        data-chart-trade-entry-price={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.find(
                (overlay) =>
                  String(overlay?.kind || "").toLowerCase() === "trade_setup",
              )?.entry_price
            : undefined
        }
        data-chart-trade-target-price={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.find(
                (overlay) =>
                  String(overlay?.kind || "").toLowerCase() === "trade_setup",
              )?.price_target
            : undefined
        }
        data-chart-trade-stop-price={
          showAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.find(
                (overlay) =>
                  String(overlay?.kind || "").toLowerCase() === "trade_setup",
              )?.stop_loss
            : undefined
        }
        data-chart-editable-overlay-count={
          editableAgenticOverlays && Array.isArray(agenticChartState?.overlays)
            ? agenticChartState.overlays.filter((overlay) =>
                CHART_EDITABLE_OVERLAY_KINDS.includes(
                  String(overlay?.kind || "").toLowerCase(),
                ),
              ).length
            : 0
        }
        data-chart-drawing-mode={
          agenticOverlayDrawingMode?.kind || undefined
        }
        data-chart-selected-overlay-id={
          selectedAgenticOverlay?.id || undefined
        }
        onPointerDownCapture={(event) => {
          overlayPointerHandlersRef.current.pointerDown?.(event.nativeEvent);
        }}
        onKeyDownCapture={(event) => {
          overlayPointerHandlersRef.current.keyDown?.(event.nativeEvent);
        }}
        onContextMenuCapture={(event) => {
          overlayPointerHandlersRef.current.contextMenu?.(event.nativeEvent);
        }}
        tabIndex={editableAgenticOverlays ? 0 : undefined}
        style={{ ...resolvedStyle, minWidth: 0, flex: "1 1 0" }}
      >
        <div
          ref={containerRef}
          data-chart-native-gesture-surface
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
          }}
        />
        <canvas
          ref={tradeOverlayCanvasRef}
          data-chart-overlay-canvas
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 3,
          }}
        />
        {tradeExecutionTargets.map((target) => {
          const overlay = agenticOverlayConfigRef.current?.editableOverlays?.find(
            (row) => String(row?.id) === String(target.id),
          );
          const side = String(overlay?.direction || "long").toLowerCase();
          const action = target.node === "entry"
            ? side === "short" ? "Short entry" : "Long entry"
            : side === "short" ? "Exit short" : "Exit long";
          return (
            <button
              key={`${target.id}:${target.node}`}
              type="button"
              data-chart-trade-execution-node={target.node}
              data-chart-trade-execution-id={target.id}
              aria-label={`${action}. Open simulated trade details.`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => selectExecutionTarget(target)}
              style={{
                position: "absolute",
                zIndex: 4,
                left: target.x - 18,
                top: target.y - 18,
                width: 36,
                height: 36,
                padding: 0,
                border: 0,
                borderRadius: "50%",
                background: "rgba(0, 0, 0, 0.001)",
                cursor: "pointer",
              }}
            />
          );
        })}
        {editableAgenticOverlays && selectedAgenticOverlay && (selectedTrade || !drawingDetailsOpen) ? (
          <div
            data-chart-drawing-toolbar
            data-chart-drawing-selection={selectedAgenticOverlay.kind}
            data-chart-trade-details={selectedTrade ? true : undefined}
            data-chart-trade-status={selectedTrade ? selectedTradeStatus : undefined}
            data-chart-trade-quantity={selectedTrade?.quantity ?? undefined}
            data-chart-trade-pnl={selectedTradeCurrencyPnl || undefined}
            role="region"
            aria-label={`${selectedAgenticOverlay.label} drawing controls`}
            style={{
              position: mobileSelectionSheet ? "fixed" : "absolute",
              top: mobileSelectionSheet ? "auto" : 44,
              bottom: mobileSelectionSheet ? 12 : "auto",
              right: mobileSelectionSheet ? 8 : selectedTrade ? 56 : 12,
              left: mobileSelectionSheet ? 8 : selectedTrade ? "auto" : 56,
              zIndex: mobileSelectionSheet ? 40 : 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: "8px",
              width: mobileSelectionSheet ? "auto" : selectedTrade ? "322px" : "fit-content",
              maxWidth: selectedTrade || mobileSelectionSheet ? undefined : "calc(100% - 80px)",
              maxHeight: mobileSelectionSheet
                ? "calc(100vh - 96px)"
                : "calc(100% - 62px)",
              overflowY: selectedTrade ? "auto" : "visible",
              padding: selectedTrade ? "12px" : "6px",
              borderRadius: "12px",
              border: `1px solid ${getPalette().border}`,
              background: isDarkMode
                ? "rgba(24, 29, 38, 0.94)"
                : "rgba(255, 255, 255, 0.94)",
              color: getPalette().text,
              boxShadow: "0 8px 20px rgba(0, 0, 0, 0.22)",
              pointerEvents: "auto",
              fontSize: "11px",
              fontWeight: 650,
              lineHeight: 1.25,
            }}
          >
            {selectedTrade ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color:
                          selectedTradeStatus === "closed"
                            ? "#fbbf24"
                            : selectedTradeStatus === "active"
                              ? getPalette().entry
                              : "#60a5fa",
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                      }}
                    >
                      {selectedTradeIsDraft
                        ? `DRAFT ${String(selectedTrade.direction || "long").toUpperCase()}`
                        : selectedTradeStatus === "pending_entry"
                          ? "WAITING FOR ENTRY"
                          : selectedTradeStatus === "active"
                            ? `OPEN ${String(selectedTrade.direction || "long").toUpperCase()}`
                            : `CLOSED ${String(selectedTrade.direction || "long").toUpperCase()}`}
                    </div>
                    <div style={{ marginTop: 3, color: getPalette().text, fontSize: 13, fontWeight: 800 }}>
                      Simulated trade
                    </div>
                    <div style={{ marginTop: 2, color: getPalette().mutedText, fontSize: 9.5 }}>
                      Paper only. No brokerage order.
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label="Close simulated trade details"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => selectEditableOverlay(null)}
                    style={{
                      height: "24px",
                      width: "24px",
                      padding: 0,
                      border: 0,
                      borderRadius: "50%",
                      background: "transparent",
                      color: getPalette().mutedText,
                      cursor: "pointer",
                      fontSize: 17,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 6,
                  }}
                >
                  {[
                    ["Entry", formatTradePrice(selectedTrade.fill_price ?? selectedTrade.entry_price)],
                    [selectedTradeStatus === "closed" ? "Exit" : "Mark", formatTradePrice(selectedTradeMark)],
                    ["Target", formatTradePrice(selectedTrade.price_target)],
                    ["Stop", formatTradePrice(selectedTrade.stop_loss)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        minWidth: 0,
                        padding: "7px 8px",
                        borderRadius: 7,
                        background: isDarkMode ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.045)",
                      }}
                    >
                      <div style={{ color: getPalette().mutedText, fontSize: 8.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {label}
                      </div>
                      <div style={{ marginTop: 2, color: getPalette().text, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1.2fr)", gap: 6 }}>
                  <label
                    style={{
                      minWidth: 0,
                      padding: "7px 8px",
                      borderRadius: 7,
                      background: isDarkMode ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.045)",
                    }}
                  >
                    <span style={{ display: "block", color: getPalette().mutedText, fontSize: 8.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Quantity
                    </span>
                    {selectedTradeIsDraft && selectedTradeStatus !== "active" ? (
                      <input
                        key={`${selectedTrade.id}:${selectedTrade.quantity}`}
                        data-chart-trade-quantity-input
                        aria-label="Simulated trade quantity"
                        type="number"
                        min="0.000001"
                        max="1000000000"
                        step="any"
                        defaultValue={formatTradeQuantity(selectedTrade.quantity)}
                        onPointerDown={(event) => event.stopPropagation()}
                        onBlur={commitTradeQuantity}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") event.currentTarget.blur();
                        }}
                        style={{
                          width: "100%",
                          marginTop: 2,
                          padding: 0,
                          border: 0,
                          outline: 0,
                          background: "transparent",
                          color: getPalette().text,
                          font: "inherit",
                          fontSize: 11,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      />
                    ) : (
                      <span style={{ display: "block", marginTop: 2, color: getPalette().text, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                        {formatTradeQuantity(selectedTrade.quantity)}
                      </span>
                    )}
                  </label>
                  <div
                    style={{
                      minWidth: 0,
                      padding: "7px 8px",
                      borderRadius: 7,
                      background: isDarkMode ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.045)",
                    }}
                  >
                    <div style={{ color: getPalette().mutedText, fontSize: 8.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {selectedTradeStatus === "closed" ? "Realized P&L" : "P&L"}
                    </div>
                    <div
                      data-chart-trade-pnl-value
                      style={{
                        marginTop: 2,
                        color:
                          (selectedTradeMetrics?.monetaryChange ?? selectedTradeMetrics?.pointChange ?? 0) < 0
                            ? getPalette().down
                            : getPalette().entry,
                        fontSize: 11,
                        fontWeight: 800,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {selectedTradeCurrencyPnl || selectedTradePercentPnl || "Not filled"}
                      {selectedTradeCurrencyPnl && selectedTradePercentPnl
                        ? `  ${selectedTradePercentPnl}`
                        : ""}
                    </div>
                  </div>
                </div>
                {selectedTradeStatus === "closed" ? (
                  <div style={{ color: getPalette().mutedText, fontSize: 9.5, lineHeight: 1.45 }}>
                    <strong style={{ color: "#fbbf24" }}>
                      {selectedTrade.exit_reason === "price_target" ? "Target hit" : "Stop hit"}
                    </strong>
                    {formatTradeTimestamp(selectedTrade.activated_at)
                      ? ` · Opened ${formatTradeTimestamp(selectedTrade.activated_at)}`
                      : ""}
                    {formatTradeTimestamp(selectedTrade.closed_at)
                      ? ` · Closed ${formatTradeTimestamp(selectedTrade.closed_at)}`
                      : ""}
                  </div>
                ) : (
                  <div style={{ color: getPalette().mutedText, fontSize: 9.5, lineHeight: 1.4 }}>
                    {selectedTradeIsDraft
                      ? selectedTradeStatus === "active"
                        ? "Drag TP and SL, then submit. Fill and quantity stay locked."
                        : "Drag entry, TP, and SL. Set quantity before submitting."
                      : selectedTradeStatus === "active"
                        ? "TP and SL are active and can still be adjusted."
                        : "Entry, TP, and SL remain queued until the entry fills."}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                  {selectedTradeIsDraft ? (
                    <>
                      <button
                        type="button"
                        data-chart-trade-draft-cancel
                        aria-label="Cancel simulated trade setup"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={cancelDraftOverlay}
                        style={{ height: 27, padding: "0 10px", border: `1px solid ${getPalette().border}`, borderRadius: 999, background: "transparent", color: getPalette().mutedText, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        data-chart-trade-draft-submit
                        aria-label="Submit simulated trade setup"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={submitDraftOverlay}
                        style={{ height: 27, padding: "0 11px", border: 0, borderRadius: 999, background: getPalette().entry, color: "#ffffff", cursor: "pointer", fontFamily: "inherit", fontSize: 10, fontWeight: 750 }}
                      >
                        Submit
                      </button>
                    </>
                  ) : (
                    <>
                      {selectedTradeStatus !== "closed" ? (
                        <button
                          type="button"
                          data-chart-trade-adjust
                          aria-label="Adjust simulated trade target and stop"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={startDraftOverlay}
                          style={{ height: 27, padding: "0 10px", border: `1px solid ${getPalette().border}`, borderRadius: 999, background: "transparent", color: getPalette().text, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}
                        >
                          Adjust TP / SL
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-label={`Delete ${selectedAgenticOverlay.label}`}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={deleteSelectedOverlay}
                        style={{ height: 27, padding: "0 10px", border: 0, borderRadius: 999, background: isDarkMode ? "rgba(248, 113, 113, 0.16)" : "rgba(220, 38, 38, 0.10)", color: getPalette().down, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <ChartDrawingToolbar
                key={selectedAgenticOverlay.id}
                busy={agenticOverlayBusy}
                overlay={selectedAgenticOverlay}
                palette={getPalette()}
                planContext={selectedDrawingPlanContext}
                tickSize={agenticOverlayTickSize}
                onChange={changeSelectedOverlay}
                onClose={() => selectEditableOverlay(null)}
                onDetails={setDrawingDetailsTab}
              />
            )}
          </div>
        ) : null}
        <div
          ref={volumeCandleTimeLabelRef}
          data-volume-candle-time-label
          style={{
            position: "absolute",
            bottom: 0,
            zIndex: 4,
            display: "none",
            pointerEvents: "none",
            transform: "translateX(-50%)",
            padding: "4px 7px",
            borderRadius: "4px",
            background: getPalette().text,
            color: getPalette().background,
            fontSize: "11px",
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        />
        <div
          ref={livePriceLabelRef}
          data-chart-current-price-label
          style={{
            position: "absolute",
            right: 0,
            zIndex: 4,
            display: "none",
            pointerEvents: "none",
            transform: "translateY(-50%)",
            padding: isMobile ? "4px 7px" : "5px 8px",
            borderTopLeftRadius: "6px",
            borderBottomLeftRadius: "6px",
            border: "none",
            background: getPalette().up,
            color: "#ffffff",
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "0.03em",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
            alignItems: "flex-start",
          }}
        />
        <div
          ref={ohlcRef}
          style={{
            position: "absolute",
            top: isMobile ? "24px" : 0,
            left: isMobile ? "2px" : "50%",
            transform: isMobile ? "none" : "translateX(-50%)",
            zIndex: 4,
            display: "flex",
            gap: isMobile ? "6px" : "10px",
            alignItems: "center",
            pointerEvents: "none",
            padding: isMobile ? "3px 7px" : "5px 10px",
            borderRadius: "999px",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid rgba(15,23,42,0.08)",
            background: isDarkMode
              ? "rgba(11, 13, 16, 0.72)"
              : "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(8px)",
            boxShadow: isDarkMode
              ? "0 8px 18px rgba(0,0,0,0.35)"
              : "0 8px 18px rgba(15,23,42,0.12)",
            color: getPalette().text,
            fontSize: isMobile ? "10px" : "12px",
            fontWeight: 700,
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
          }}
        />
      </div>
      {drawingDetailsOpen ? <aside data-chart-drawing-details-panel data-chart-drawing-details-placement={dockDrawingDetails ? "docked" : "sheet"}
        aria-label="Drawing details" onPointerDown={(event) => event.stopPropagation()}
        style={{ boxSizing: "border-box", zIndex: 40, padding: 14, overflowY: "auto", background: getPalette().background, color: getPalette().text,
          borderLeft: `1px solid ${getPalette().border}`, ...(dockDrawingDetails
            ? { width: 308, flex: "0 0 308px", height: "100%" }
            : { position: "fixed", bottom: 8, left: 8, right: 8, maxHeight: "58dvh", border: `1px solid ${getPalette().border}`, borderRadius: 14, boxShadow: "0 -6px 24px rgba(0,0,0,0.15)" }) }}>
        <ChartDrawingInspector key={`${selectedAgenticOverlay.id}:${drawingDetailsTab}`} initialTab={drawingDetailsTab}
          busy={agenticOverlayBusy} contextLabel={agenticOverlayContextLabel} dark={isDarkMode}
          overlay={selectedAgenticOverlay} palette={getPalette()} planContext={selectedDrawingPlanContext} tickSize={agenticOverlayTickSize}
          onAsk={onAgenticOverlayAsk ? askAboutSelectedOverlay : null} onChange={changeSelectedOverlay}
          onClose={() => { setDrawingDetailsTab(null); chartRootRef.current?.focus({ preventScroll: true }); }}
          onCopy={isCopyableEditableOverlay(selectedAgenticOverlay) ? copySelectedOverlay : null}
          onDelete={deleteSelectedOverlay} onHide={hideSelectedOverlay}
          onMonitor={selectedDrawingPlanContext?.canMonitor ? monitorSelectedOverlay : null}
          onUseInPlan={selectedDrawingPlanContext?.canUseInPlan ? useSelectedOverlayInPlan : null}
          onReviewPlan={onAgenticOverlayReviewPlan ? async () => { const result = await onAgenticOverlayReviewPlan(); if (result !== false) setDrawingDetailsTab(null); return result; } : null} />
      </aside> : null}
      </div>
    );
  },
  (prev, next) => {
    if (prev.idx !== next.idx) return false;
    if (prev.tickerPrices !== next.tickerPrices) return false;
    if (prev.selectedScale !== next.selectedScale) return false;
    if (prev.chartViewType !== next.chartViewType) return false;
    if (prev.isChartVisible !== next.isChartVisible) return false;
    if (prev.sharedRangeBus !== next.sharedRangeBus) return false;
    if (prev.rangeTransfer !== next.rangeTransfer) return false;
    if (prev.preferUpdatedDisplayRange !== next.preferUpdatedDisplayRange) return false;
    if (prev.onRequestHistory !== next.onRequestHistory) return false;
    if (prev.historyLoading !== next.historyLoading) return false;
    if (prev.historyHasMore !== next.historyHasMore) return false;
    if (prev.selectedTickerId !== next.selectedTickerId) return false;
    if (prev.tradeOverlayKey !== next.tradeOverlayKey) return false;
    if (prev.agenticOverlayKey !== next.agenticOverlayKey) return false;
    if (prev.agenticChartState !== next.agenticChartState) return false;
    if (prev.compactAgenticLabels !== next.compactAgenticLabels) return false;
    if (prev.explainBarChange !== next.explainBarChange) return false;
    if (prev.showIndicators !== next.showIndicators) return false;
    if (prev.showAgenticOverlays !== next.showAgenticOverlays) return false;
    if (prev.editableAgenticOverlays !== next.editableAgenticOverlays) return false;
    if (prev.agenticOverlayDrawingMode !== next.agenticOverlayDrawingMode) return false;
    if (prev.onAgenticOverlayCreate !== next.onAgenticOverlayCreate) return false;
    if (prev.onAgenticOverlayChange !== next.onAgenticOverlayChange) return false;
    if (prev.onAgenticOverlayDelete !== next.onAgenticOverlayDelete) return false;
    if (prev.onAgenticOverlayHide !== next.onAgenticOverlayHide) return false;
    if (prev.onAgenticOverlayAsk !== next.onAgenticOverlayAsk) return false;
    if (prev.onAgenticOverlayUseInPlan !== next.onAgenticOverlayUseInPlan) return false;
    if (prev.onAgenticOverlayCreateMonitor !== next.onAgenticOverlayCreateMonitor) return false;
    if (prev.resolveAgenticOverlayPlanContext !== next.resolveAgenticOverlayPlanContext) return false;
    if (prev.agenticOverlayContextLabel !== next.agenticOverlayContextLabel) return false;
    if (prev.agenticOverlayBusy !== next.agenticOverlayBusy) return false;
    if (prev.agenticOverlayTickSize !== next.agenticOverlayTickSize) return false;
    if (prev.onDrawingUndo !== next.onDrawingUndo) return false;
    if (prev.onDrawingRedo !== next.onDrawingRedo) return false;
    if (prev.onAgenticOverlayReviewPlan !== next.onAgenticOverlayReviewPlan) return false;
    if (prev.draftAgenticOverlayId !== next.draftAgenticOverlayId) return false;
    if (prev.onAgenticOverlayDraftSubmit !== next.onAgenticOverlayDraftSubmit) return false;
    if (prev.onAgenticOverlayDraftCancel !== next.onAgenticOverlayDraftCancel) return false;
    if (prev.onAgenticOverlayDraftStart !== next.onAgenticOverlayDraftStart) return false;
    if (
      prev.onAgenticOverlayDrawingCancel !==
      next.onAgenticOverlayDrawingCancel
    ) {
      return false;
    }
    if (prev.canvasStyle !== next.canvasStyle) return false;
    if (prev.assetType !== next.assetType) return false;
    if (prev.extendedHoursQuote !== next.extendedHoursQuote) return false;
    if (prev.livePriceQuote !== next.livePriceQuote) return false;
    if (prev.priceMarkers !== next.priceMarkers) return false;
    if (prev.chartConfigKey !== next.chartConfigKey) return false;
    if (prev.onChartReady !== next.onChartReady) return false;
    if (prev.userTimezone !== next.userTimezone) return false;
    return true;
  }
);

ChartCanvas.displayName = "ChartCanvas";

export default ChartCanvas;
